import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  LayoutDashboard, Package, ShoppingCart, Users, 
  Plus, Edit, Trash2, LogOut, Menu, Search,
  DollarSign, Loader2, Upload, RefreshCw, Tag, Percent, ImagePlus,
  Flame, Shield, UserPlus, Eye, EyeOff, CheckSquare, Square, ChevronLeft, ChevronRight, X, BarChart3, TrendingUp, Construction, CircleDollarSign, Activity, Megaphone, Mail, Copy, Globe, Gift, ExternalLink, FileText, LayoutList, Bell, Download, Sparkles, Edit2, Monitor, Settings, ToggleLeft, ToggleRight, Check, CreditCard
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

// Extracted tab components
import EarningsTab from '../components/admin/EarningsTab';
import SEOTab from '../components/admin/SEOTab';
import CategoriesTab from '../components/admin/CategoriesTab';
import NotificationsTab from '../components/admin/NotificationsTab';
import SnapshotsTab from '../components/admin/SnapshotsTab';
import SettingsTab from '../components/admin/SettingsTab';
import PricingTab from '../components/admin/PricingTab';
import MarketingTab from '../components/admin/MarketingTab';
import BlogTab from '../components/admin/BlogTab';
import PromotionsCodesSection from '../components/admin/PromotionsCodesSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SIZES = [
  { size: "4x6", price: 11.99 },
  { size: "13x19", price: 22.99 },
  { size: "24x36", price: 32.99 },
  { size: "24x40", price: 49.99 },
  { size: "36x48", price: 134.99 }
];

const ROLE_COLORS = {
  admin: 'bg-red-600', manager: 'bg-purple-600', editor: 'bg-blue-600',
  inventory: 'bg-yellow-600', viewer: 'bg-gray-500', customer: 'bg-green-600'
};

const PERMISSIONS_MAP = {
  viewer: ["view_products", "view_orders"],
  inventory: ["view_products", "edit_products"],
  editor: ["view_products", "view_orders", "edit_products", "manage_promotions"],
  manager: ["view_products", "view_orders", "edit_products", "manage_promotions", "manage_orders"],
  admin: ["view_products", "view_orders", "edit_products", "manage_promotions", "manage_orders", "manage_users", "manage_settings"]
};





// ─── AI Upload Tab Component ──────────────────────────
const AIUploadTab = ({ token, categories, onProductAdded }) => {
  const [files, setFiles] = useState([]);
  const [results, setResults] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const handleFiles = (fileList) => {
    const valid = Array.from(fileList).filter(f =>
      ['image/jpeg', 'image/png', 'image/webp'].includes(f.type) && f.size <= 20 * 1024 * 1024
    );
    if (valid.length < fileList.length) {
      toast.error(`${fileList.length - valid.length} file(s) skipped (unsupported format or >20MB)`);
    }
    setFiles(prev => [...prev, ...valid]);
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const analyzeAll = async () => {
    if (files.length === 0) return;
    setAnalyzing(true);
    setProgress({ current: 0, total: files.length });
    const newResults = [];

    for (let i = 0; i < files.length; i++) {
      setProgress({ current: i + 1, total: files.length });
      try {
        const formData = new FormData();
        formData.append('file', files[i]);
        const res = await axios.post(`${API}/admin/ai-analyze-poster`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
          timeout: 60000
        });
        newResults.push({
          filename: files[i].name,
          preview: URL.createObjectURL(files[i]),
          image_url: res.data.image_url,
          ...res.data.analysis,
          added: false,
          error: res.data.error || null
        });
      } catch (err) {
        newResults.push({
          filename: files[i].name,
          preview: URL.createObjectURL(files[i]),
          image_url: '',
          title: files[i].name.replace(/\.\w+$/, ''),
          category: categories[0]?.id || '',
          subcategory: '',
          description: 'Premium poster print',
          tags: [],
          artist: '',
          added: false,
          error: err.message
        });
      }
    }

    setResults(newResults);
    setFiles([]);
    setAnalyzing(false);
  };

  const updateResult = (idx, field, value) => {
    setResults(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const addToInventory = async (idx) => {
    const r = results[idx];
    try {
      // Check if category exists, if not create it
      const catId = (r.category || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      const existingCat = categories.find(c => c.id === catId || c.name.toLowerCase() === (r.category || '').toLowerCase());

      if (!existingCat && catId) {
        const catName = r.category.trim();
        await axios.post(`${API}/admin/categories`, {
          id: catId,
          name: catName,
          seo_title: `${catName} Posters | PosterFire`,
          seo_description: `Shop premium ${catName.toLowerCase()} posters and wall art at PosterFire.com.`,
          seo_keywords: `${catName.toLowerCase()} posters, wall art, prints`
        }, { headers });
        toast.success(`New category "${catName}" created with SEO!`);
      }

      const finalCatId = existingCat?.id || catId;

      // Check if subcategory exists, if not create it
      if (r.subcategory && r.subcategory.trim()) {
        const subId = r.subcategory.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        const subName = r.subcategory.trim();
        const catForSub = existingCat || { subcategories: [] };
        const existingSub = (catForSub.subcategories || []).find(s => s.id === subId || s.name.toLowerCase() === subName.toLowerCase());
        if (!existingSub) {
          await axios.post(`${API}/admin/categories/${finalCatId}/subcategories`, {
            id: `${finalCatId}_${subId}`,
            name: subName
          }, { headers });
          toast.success(`New subcategory "${subName}" created!`);
        }
      }

      await axios.post(`${API}/admin/ai-add-product`, {
        title: r.title,
        category: finalCatId,
        subcategory: r.subcategory?.trim()?.toLowerCase()?.replace(/[^a-z0-9]+/g, '_')?.replace(/^_|_$/g, '') || '',
        description: r.description,
        tags: typeof r.tags === 'string' ? r.tags.split(',').map(t => t.trim()) : r.tags,
        artist: r.artist,
        image_url: r.image_url
      }, { headers });
      updateResult(idx, 'added', true);
      toast.success(`"${r.title}" added to inventory!`);
      if (onProductAdded) onProductAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add product');
    }
  };

  const addAllToInventory = async () => {
    const pending = results.filter(r => !r.added && r.image_url);
    for (let i = 0; i < pending.length; i++) {
      const idx = results.indexOf(pending[i]);
      await addToInventory(idx);
    }
  };

  return (
    <div className="space-y-4" data-testid="ai-upload-tab">
      {/* Upload Zone */}
      {results.length === 0 && (
        <div
          className={`bg-white border-2 border-dashed rounded-lg p-10 text-center transition-colors cursor-pointer ${
            dragOver ? 'border-orange-500 bg-orange-50' : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
          onClick={() => fileInputRef.current?.click()}
          data-testid="ai-drop-zone"
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="ai-file-input"
          />
          <Upload className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-base font-bold text-gray-700 mb-1">Drop poster images here</p>
          <p className="text-sm text-gray-400">or click to browse — JPEG, PNG, WEBP up to 20MB each</p>
          <p className="text-xs text-gray-400 mt-2">AI will analyze each image to generate title, category, tags & description</p>
        </div>
      )}

      {/* File Queue */}
      {files.length > 0 && !analyzing && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3" data-testid="ai-file-queue">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-900">{files.length} image{files.length > 1 ? 's' : ''} ready</h3>
            <div className="flex gap-2">
              <button onClick={() => setFiles([])} className="text-xs text-gray-500 hover:text-red-600">Clear all</button>
              <button onClick={analyzeAll}
                className="px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold rounded-lg hover:from-red-600 hover:to-orange-600"
                data-testid="ai-analyze-btn">
                Analyze with AI
              </button>
            </div>
          </div>
          <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-2">
            {files.map((f, i) => (
              <div key={i} className="relative group aspect-[3/4] bg-gray-100 rounded overflow-hidden">
                <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                <button onClick={() => removeFile(i)}
                  className="absolute top-0.5 right-0.5 w-4 h-4 bg-black/60 text-white rounded-full text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analyzing Progress */}
      {analyzing && (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center" data-testid="ai-progress">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-3" />
          <p className="text-sm font-bold text-gray-900">Analyzing poster {progress.current} of {progress.total}...</p>
          <div className="w-64 mx-auto mt-3 bg-gray-200 rounded-full h-2">
            <div className="bg-gradient-to-r from-red-500 to-orange-500 h-2 rounded-full transition-all"
              style={{ width: `${(progress.current / progress.total) * 100}%` }}></div>
          </div>
        </div>
      )}

      {/* Results */}
      {results.length > 0 && !analyzing && (
        <div className="space-y-3" data-testid="ai-results">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-900">
              {results.length} poster{results.length > 1 ? 's' : ''} analyzed
              <span className="text-gray-400 font-normal ml-2">
                ({results.filter(r => r.added).length} added)
              </span>
            </h3>
            <div className="flex gap-2">
              <button onClick={() => { setResults([]); setFiles([]); }}
                className="text-xs text-gray-500 hover:text-red-600">Start over</button>
              {results.some(r => !r.added && r.image_url) && (
                <button onClick={addAllToInventory}
                  className="px-4 py-2 bg-gray-900 text-white text-sm font-bold rounded-lg hover:bg-gray-800"
                  data-testid="ai-add-all-btn">
                  Add All to Inventory
                </button>
              )}
            </div>
          </div>

          {results.map((r, i) => (
            <div key={i} className={`bg-white border rounded-lg p-4 flex gap-4 transition-all ${r.added ? 'border-green-300 bg-green-50 opacity-70' : 'border-gray-200'}`}
              data-testid={`ai-result-${i}`}>
              {/* Preview */}
              <div className="w-24 h-32 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                <img src={r.preview || (process.env.REACT_APP_BACKEND_URL + r.image_url)} alt="" className="w-full h-full object-cover" />
              </div>
              {/* Editable Fields */}
              <div className="flex-1 space-y-2 min-w-0">
                <input value={r.title} onChange={(e) => updateResult(i, 'title', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm font-bold text-gray-900 border border-gray-200 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Title" disabled={r.added} data-testid={`ai-title-${i}`} />
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input value={r.category} onChange={(e) => updateResult(i, 'category', e.target.value)}
                      list={`cat-list-${i}`}
                      className="w-full px-2 py-1.5 text-xs font-semibold text-gray-900 border border-gray-200 rounded"
                      placeholder="Category (type or select)" disabled={r.added} data-testid={`ai-category-${i}`} />
                    <datalist id={`cat-list-${i}`}>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </datalist>
                    {!categories.find(c => c.id === r.category) && r.category && (
                      <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] font-bold bg-green-100 text-green-700 px-1 rounded">NEW</span>
                    )}
                  </div>
                  <div className="flex-1 relative">
                    <input value={r.subcategory || ''} onChange={(e) => updateResult(i, 'subcategory', e.target.value)}
                      className="w-full px-2 py-1.5 text-xs font-semibold text-gray-900 border border-gray-200 rounded"
                      placeholder="Subcategory" disabled={r.added} data-testid={`ai-subcategory-${i}`} />
                  </div>
                </div>
                <input value={r.description} onChange={(e) => updateResult(i, 'description', e.target.value)}
                  className="w-full px-2 py-1.5 text-xs font-semibold text-gray-900 border border-gray-200 rounded" placeholder="Description" disabled={r.added} />
                <div className="flex gap-2 items-center">
                  <input value={Array.isArray(r.tags) ? r.tags.join(', ') : r.tags}
                    onChange={(e) => updateResult(i, 'tags', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-xs font-semibold text-gray-900 border border-gray-200 rounded" placeholder="Tags (comma separated)" disabled={r.added} />
                  {r.error && <span className="text-[10px] text-orange-500">AI fallback</span>}
                </div>
              </div>
              {/* Actions */}
              <div className="flex-shrink-0 flex items-center">
                {r.added ? (
                  <span className="text-xs text-green-600 font-bold flex items-center gap-1">
                    <CheckSquare className="w-4 h-4" /> Added
                  </span>
                ) : (
                  <button onClick={() => addToInventory(i)}
                    className="px-3 py-2 bg-gray-900 text-white text-xs font-bold rounded-lg hover:bg-gray-800 transition-colors"
                    data-testid={`ai-add-${i}`}>
                    Add
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── AI Categorization Tab Component ──────────────────────────
const AICategorizeTab = ({ token, onCategoriesChanged }) => {
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [results, setResults] = useState(null);
  const [resultsPage, setResultsPage] = useState(1);
  const [applying, setApplying] = useState(false);
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [filterCategory, setFilterCategory] = useState('all');
  const headers = { Authorization: `Bearer ${token}` };

  const startCategorization = async () => {
    try {
      const res = await axios.post(`${API}/admin/ai-categorize`, {}, { headers });
      setJobId(res.data.job_id);
      setStatus({ status: 'running', total: 0, processed: 0 });
      setResults(null);
      setExcludedIds(new Set());
      toast.success('AI categorization started!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start');
    }
  };

  useEffect(() => {
    if (!jobId || status?.status === 'done' || status?.status === 'error') return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/admin/ai-categorize/status/${jobId}`, { headers });
        setStatus(res.data);
        if (res.data.status === 'done' || res.data.status === 'error') {
          clearInterval(interval);
          if (res.data.status === 'done') {
            const rRes = await axios.get(`${API}/admin/ai-categorize/results/${jobId}?page=1&limit=50`, { headers });
            setResults(rRes.data);
            setResultsPage(1);
          }
        }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [jobId, status?.status]);

  const loadResultsPage = async (page) => {
    try {
      const res = await axios.get(`${API}/admin/ai-categorize/results/${jobId}?page=${page}&limit=50`, { headers });
      setResults(res.data);
      setResultsPage(page);
    } catch {}
  };

  const toggleExclude = (id) => {
    setExcludedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const applyChanges = async () => {
    setApplying(true);
    try {
      const res = await axios.post(`${API}/admin/ai-categorize/apply/${jobId}`, {
        excluded_ids: Array.from(excludedIds)
      }, { headers });
      toast.success(`Applied ${res.data.applied} category changes! ${res.data.new_categories_created?.length || 0} new categories created.`);
      setJobId(null);
      setStatus(null);
      setResults(null);
      if (onCategoriesChanged) onCategoriesChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  const filteredChanges = results?.changes?.filter(c =>
    filterCategory === 'all' || c.new_category === filterCategory
  ) || [];

  const totalPages = results ? Math.ceil(results.total_changes / 50) : 0;

  return (
    <div className="space-y-4" data-testid="ai-categorize-tab">
      {/* Start Section */}
      {!jobId && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center" data-testid="categorize-start">
          <Sparkles className="w-10 h-10 text-orange-500 mx-auto mb-3" />
          <h3 className="text-base font-bold text-gray-900 mb-1">AI Bulk Categorization</h3>
          <p className="text-sm text-gray-500 mb-4 max-w-md mx-auto">
            Scan all products and automatically assign them to the best category and subcategory using AI. New categories will be created if needed.
          </p>
          <button onClick={startCategorization}
            className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold rounded-lg hover:from-red-600 hover:to-orange-600 transition-all"
            data-testid="start-categorize-btn">
            Start AI Categorization
          </button>
        </div>
      )}

      {/* Progress */}
      {status?.status === 'running' && (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center" data-testid="categorize-progress">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-3" />
          <p className="text-sm font-bold text-gray-900">
            Categorizing products... {status.processed} / {status.total || '?'}
          </p>
          {status.total > 0 && (
            <div className="w-64 mx-auto mt-3 bg-gray-200 rounded-full h-2">
              <div className="bg-gradient-to-r from-red-500 to-orange-500 h-2 rounded-full transition-all"
                style={{ width: `${Math.min((status.processed / status.total) * 100, 100)}%` }} />
            </div>
          )}
          <p className="text-xs text-gray-400 mt-2">This may take a few minutes for large inventories...</p>
        </div>
      )}

      {/* Error */}
      {status?.status === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-sm font-bold text-red-700 mb-2">Categorization failed</p>
          <p className="text-xs text-red-500">{status.error}</p>
          <button onClick={() => { setJobId(null); setStatus(null); }}
            className="mt-3 px-4 py-2 bg-red-600 text-white text-sm font-bold rounded-lg">Try Again</button>
        </div>
      )}

      {/* Results */}
      {status?.status === 'done' && results && (
        <div className="space-y-4" data-testid="categorize-results">
          {/* Summary */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-900">
                {results.total_changes} products to re-categorize
              </h3>
              <div className="flex gap-2">
                <button onClick={() => { setJobId(null); setStatus(null); setResults(null); }}
                  className="px-3 py-1.5 text-xs text-gray-500 hover:text-red-600 border border-gray-200 rounded">
                  Cancel
                </button>
                <button onClick={applyChanges} disabled={applying}
                  className="px-4 py-1.5 bg-green-600 text-white text-xs font-bold rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                  data-testid="apply-categorize-btn">
                  {applying ? <><Loader2 className="w-3 h-3 animate-spin" /> Applying...</> : `Apply ${results.total_changes - excludedIds.size} Changes`}
                </button>
              </div>
            </div>

            {/* Summary by category */}
            <div className="flex flex-wrap gap-2 mb-3">
              <button onClick={() => setFilterCategory('all')}
                className={`px-2 py-1 text-xs rounded font-medium ${filterCategory === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}>
                All ({results.total_changes})
              </button>
              {Object.entries(results.summary || {}).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                <button key={cat} onClick={() => setFilterCategory(cat)}
                  className={`px-2 py-1 text-xs rounded font-medium ${filterCategory === cat ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}>
                  {cat} ({count})
                </button>
              ))}
            </div>

            {/* New categories badge */}
            {Object.keys(results.new_categories || {}).length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-3">
                <p className="text-xs font-bold text-blue-700 mb-1">New categories to create:</p>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(results.new_categories).map(([id, name]) => (
                    <span key={id} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-medium">{name}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Changes List */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-bold text-gray-500 uppercase w-8"></th>
                  <th className="text-left px-3 py-2 text-xs font-bold text-gray-500 uppercase">Product</th>
                  <th className="text-left px-3 py-2 text-xs font-bold text-gray-500 uppercase">Old Category</th>
                  <th className="text-left px-3 py-2 text-xs font-bold text-gray-500 uppercase">New Category</th>
                  <th className="text-left px-3 py-2 text-xs font-bold text-gray-500 uppercase">Subcategory</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredChanges.map((ch) => (
                  <tr key={ch.id} className={`${excludedIds.has(ch.id) ? 'opacity-40 bg-gray-50' : 'hover:bg-gray-50'}`}>
                    <td className="px-3 py-2">
                      <input type="checkbox" checked={!excludedIds.has(ch.id)}
                        onChange={() => toggleExclude(ch.id)}
                        className="rounded border-gray-300" data-testid={`cat-check-${ch.id}`} />
                    </td>
                    <td className="px-3 py-2 text-xs font-medium text-gray-900 max-w-xs truncate">{ch.title}</td>
                    <td className="px-3 py-2">
                      <span className="text-xs text-gray-400">{ch.old_category}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-xs font-bold text-green-700 bg-green-50 px-1.5 py-0.5 rounded">{ch.new_category}</span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-xs text-gray-600">{ch.new_subcategory || '—'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 p-3 border-t border-gray-200">
                <button onClick={() => loadResultsPage(resultsPage - 1)} disabled={resultsPage <= 1}
                  className="px-2 py-1 text-xs rounded border disabled:opacity-30">Prev</button>
                <span className="text-xs text-gray-500">Page {resultsPage} / {totalPages}</span>
                <button onClick={() => loadResultsPage(resultsPage + 1)} disabled={resultsPage >= totalPages}
                  className="px-2 py-1 text-xs rounded border disabled:opacity-30">Next</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};





// ─── Construction Tab Component ──────────────────────────
const ConstructionTab = ({ token }) => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/construction`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setEnabled(r.data.construction_mode))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const handleToggle = async () => {
    setToggling(true);
    try {
      const res = await axios.put(`${API}/admin/construction?enabled=${!enabled}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setEnabled(res.data.construction_mode);
      toast.success(res.data.construction_mode ? 'Construction mode ON — site is now hidden from visitors' : 'Construction mode OFF — site is live!');
    } catch { toast.error('Failed to update'); }
    finally { setToggling(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-construction">
      <div className={`rounded p-8 text-center border-2 ${enabled ? 'bg-orange-50 border-orange-300' : 'bg-green-50 border-green-300'}`}>
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${enabled ? 'bg-orange-100' : 'bg-green-100'}`}>
          <Construction className={`w-8 h-8 ${enabled ? 'text-orange-600' : 'text-green-600'}`} />
        </div>
        <h2 className="text-2xl font-black text-gray-900 mb-2">
          {enabled ? 'Site is Under Construction' : 'Site is Live'}
        </h2>
        <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">
          {enabled
            ? 'Visitors will see an "Under Construction" page. You can still access the admin panel and login page.'
            : 'Your site is publicly accessible. Toggle construction mode to hide it from visitors while you make changes.'}
        </p>
        <button onClick={handleToggle} disabled={toggling}
          className={`px-8 py-3 text-sm font-bold uppercase tracking-wider rounded transition-colors disabled:opacity-50 ${
            enabled
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-orange-600 text-white hover:bg-orange-700'
          }`}
          data-testid="toggle-construction">
          {toggling ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}
          {enabled ? 'Go Live — Disable Construction Mode' : 'Enable Construction Mode'}
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded p-5">
        <h3 className="text-sm font-bold text-gray-900 mb-3">How It Works</h3>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded bg-gray-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-gray-500 mt-0.5">1</span>
            When enabled, all public pages show an "Under Construction" landing page
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded bg-gray-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-gray-500 mt-0.5">2</span>
            The admin panel (/admin) and login page (/login) remain accessible
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded bg-gray-100 flex items-center justify-center flex-shrink-0 text-xs font-bold text-gray-500 mt-0.5">3</span>
            Come back here to disable construction mode when you're ready to go live
          </li>
        </ul>
      </div>
    </div>
  );
};


// ─── User Activity Panel ──────────────────────────
const UserActivityPanel = ({ token }) => {
  const [activity, setActivity] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/user-activity`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setActivity(r.data))
      .catch(() => {});
  }, [token]);

  if (!activity) return null;

  return (
    <div className="bg-white border border-gray-200 rounded p-4" data-testid="user-activity-panel">
      <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
        <Activity className="w-4 h-4 text-blue-600" /> User Activity (Last 7 Days)
      </h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="bg-gray-50 rounded p-3">
          <p className="text-2xl font-black text-gray-900" data-testid="active-today">{activity.active_today}</p>
          <p className="text-xs text-gray-500">Active today</p>
        </div>
        <div className="bg-gray-50 rounded p-3">
          <p className="text-2xl font-black text-gray-900">
            {activity.daily.reduce((s, d) => s + d.unique_users, 0)}
          </p>
          <p className="text-xs text-gray-500">Unique users (7d)</p>
        </div>
        <div className="bg-gray-50 rounded p-3">
          <p className="text-2xl font-black text-gray-900">
            {activity.daily.reduce((s, d) => s + d.actions, 0).toLocaleString()}
          </p>
          <p className="text-xs text-gray-500">Total actions (7d)</p>
        </div>
        <div className="bg-gray-50 rounded p-3">
          <p className="text-2xl font-black text-gray-900">{activity.top_pages.length}</p>
          <p className="text-xs text-gray-500">Pages visited</p>
        </div>
      </div>

      {/* Activity Chart */}
      {activity.daily.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-gray-500 font-semibold uppercase mb-2">Daily Actions</p>
          <div className="flex items-end gap-1 h-16">
            {activity.daily.map((d, i) => {
              const max = Math.max(...activity.daily.map(x => x.actions), 1);
              const h = (d.actions / max) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative">
                  <div className="w-full bg-blue-400 rounded-t min-h-[2px] hover:bg-blue-600 cursor-pointer transition-colors"
                    style={{ height: `${Math.max(h, 3)}%` }} />
                  <span className="text-[9px] text-gray-400">{d.date.slice(-5)}</span>
                  <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                    {d.actions} actions, {d.unique_users} users
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top Pages */}
      {activity.top_pages.length > 0 && (
        <div>
          <p className="text-xs text-gray-500 font-semibold uppercase mb-2">Top Pages</p>
          <div className="flex flex-wrap gap-1.5">
            {activity.top_pages.slice(0, 8).map((p, i) => (
              <span key={i} className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-700">
                {p.page} <span className="text-gray-400">({p.views})</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// ─── Visit Counter Widget ──────────────────────────



// ─── World Map Widget ──────────────────────────
const COUNTRY_DATA = {
  US: { name: 'United States', x: 170, y: 145 },
  GB: { name: 'United Kingdom', x: 375, y: 105 },
  CA: { name: 'Canada', x: 175, y: 100 },
  AU: { name: 'Australia', x: 660, y: 280 },
  DE: { name: 'Germany', x: 400, y: 115 },
  FR: { name: 'France', x: 385, y: 125 },
  JP: { name: 'Japan', x: 680, y: 145 },
  BR: { name: 'Brazil', x: 265, y: 260 },
  IN: { name: 'India', x: 560, y: 185 },
  MX: { name: 'Mexico', x: 155, y: 185 },
  IT: { name: 'Italy', x: 402, y: 135 },
  ES: { name: 'Spain', x: 370, y: 140 },
  CN: { name: 'China', x: 620, y: 150 },
  KR: { name: 'South Korea', x: 660, y: 145 },
  NL: { name: 'Netherlands', x: 390, y: 108 },
  SE: { name: 'Sweden', x: 410, y: 80 },
  RU: { name: 'Russia', x: 520, y: 90 },
  ZA: { name: 'South Africa', x: 440, y: 290 },
  NG: { name: 'Nigeria', x: 395, y: 210 },
  PH: { name: 'Philippines', x: 650, y: 200 },
};

const WorldMapWidget = ({ token }) => {
  const [mapData, setMapData] = React.useState(null);
  const [sessions, setSessions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [hoveredPin, setHoveredPin] = React.useState(null);
  const [hoveredState, setHoveredState] = React.useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/visits/na-map`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/admin/sessions`, { headers: { Authorization: `Bearer ${token}` } })
    ]).then(([mapRes, sessRes]) => {
      setMapData(mapRes.data);
      setSessions(sessRes.data.sessions || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="flex items-center justify-center h-48"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;

  const locations = mapData?.locations || [];
  const regionCounts = mapData?.region_counts || {};
  const totalNA = mapData?.total_na || 0;
  const total = mapData?.total || 0;
  const maxCount = Math.max(1, ...locations.map(l => l.count));

  // Albers-like projection for continental US
  const project = (lat, lng) => {
    const x = ((lng + 130) / 65) * 960;
    const y = ((52 - lat) / 28) * 600;
    return [Math.max(0, Math.min(960, x)), Math.max(0, Math.min(600, y))];
  };

  // US state paths (simplified but accurate)
  const states = [
    { id: 'WA', d: 'M108,25 L170,18 L182,20 L184,28 L192,36 L193,52 L178,80 L152,82 L122,80 L108,74 L96,60 L95,40Z' },
    { id: 'OR', d: 'M96,60 L108,74 L122,80 L152,82 L160,96 L160,130 L100,134 L78,124 L68,106 L66,82Z' },
    { id: 'CA', d: 'M66,82 L68,106 L78,124 L100,134 L100,152 L88,184 L82,220 L92,256 L78,276 L56,264 L42,240 L38,200 L36,160 L42,120 L52,92Z' },
    { id: 'NV', d: 'M100,134 L160,130 L170,146 L180,192 L166,224 L118,218 L100,152Z' },
    { id: 'ID', d: 'M160,30 L192,36 L200,58 L212,76 L212,96 L208,120 L196,138 L176,140 L160,130 L160,96 L152,82 L160,60Z' },
    { id: 'MT', d: 'M192,36 L310,22 L318,28 L320,62 L316,78 L300,82 L260,80 L230,82 L212,76 L200,58Z' },
    { id: 'WY', d: 'M212,76 L260,80 L300,82 L316,78 L320,96 L320,140 L264,144 L224,142 L212,140 L212,96Z' },
    { id: 'UT', d: 'M160,130 L176,140 L196,138 L212,140 L224,142 L226,180 L224,202 L180,192 L170,146Z' },
    { id: 'CO', d: 'M224,142 L264,144 L320,140 L340,142 L340,198 L282,198 L226,198 L226,180Z' },
    { id: 'AZ', d: 'M100,218 L118,218 L166,224 L180,192 L224,202 L226,198 L224,268 L196,288 L154,296 L126,296 L96,276 L92,256Z' },
    { id: 'NM', d: 'M226,198 L282,198 L340,198 L340,270 L330,296 L260,296 L224,296 L224,268Z' },
    { id: 'ND', d: 'M310,22 L410,16 L422,22 L424,62 L418,70 L370,70 L320,68 L320,62 L318,28Z' },
    { id: 'SD', d: 'M320,68 L370,70 L418,70 L424,72 L426,114 L420,120 L372,120 L324,118 L320,96Z' },
    { id: 'NE', d: 'M320,118 L324,118 L372,120 L420,120 L432,122 L448,140 L448,156 L392,156 L340,154 L340,142 L320,140Z' },
    { id: 'KS', d: 'M340,154 L392,156 L448,156 L480,160 L480,210 L420,210 L340,210 L340,198Z' },
    { id: 'OK', d: 'M340,210 L420,210 L480,210 L506,214 L524,208 L530,216 L530,236 L512,244 L480,250 L424,260 L376,260 L340,268 L340,270Z' },
    { id: 'TX', d: 'M260,296 L330,296 L340,270 L376,260 L424,260 L480,250 L512,244 L520,260 L528,290 L536,328 L530,360 L518,388 L504,404 L480,412 L454,412 L420,400 L396,384 L362,364 L334,352 L308,340 L282,328 L260,316Z' },
    { id: 'MN', d: 'M422,22 L480,18 L498,14 L502,30 L510,50 L510,68 L502,82 L492,96 L480,106 L460,110 L432,112 L426,114 L424,72 L424,62Z' },
    { id: 'IA', d: 'M432,112 L460,110 L480,106 L502,108 L530,110 L536,126 L536,156 L520,160 L480,160 L448,156 L448,140 L432,122Z' },
    { id: 'MO', d: 'M480,160 L520,160 L536,156 L562,160 L578,166 L586,184 L582,206 L572,224 L550,230 L528,228 L506,214 L480,210Z' },
    { id: 'AR', d: 'M506,214 L528,228 L550,230 L564,238 L570,258 L568,278 L540,280 L524,274 L524,236 L530,216Z' },
    { id: 'LA', d: 'M524,274 L540,280 L568,278 L570,290 L580,306 L590,316 L596,332 L580,342 L562,340 L540,344 L522,338 L518,326 L528,310 L524,290Z' },
    { id: 'WI', d: 'M502,30 L540,24 L560,28 L578,36 L586,52 L582,72 L574,92 L560,104 L542,110 L530,110 L502,108 L492,96 L502,82 L510,68 L510,50Z' },
    { id: 'IL', d: 'M536,126 L562,120 L574,124 L582,136 L586,160 L586,184 L578,166 L562,160 L536,156Z' },
    { id: 'MI', d: 'M578,20 L604,14 L622,22 L630,40 L626,52 L618,62 L612,52 L600,44 L590,50 L586,52 L578,36 L582,28Z M600,60 L618,62 L634,66 L640,80 L630,98 L616,106 L604,112 L590,108 L582,104 L574,92 L582,72 L590,62Z' },
    { id: 'IN', d: 'M582,104 L604,112 L616,106 L624,118 L624,142 L620,168 L604,172 L590,168 L582,156 L582,136 L574,124Z' },
    { id: 'OH', d: 'M616,106 L630,98 L650,96 L672,92 L686,100 L690,118 L686,140 L672,152 L654,160 L636,164 L620,168 L624,142 L624,118Z' },
    { id: 'KY', d: 'M586,160 L604,172 L620,168 L636,164 L654,160 L672,152 L692,158 L704,164 L704,186 L688,196 L660,198 L632,196 L608,194 L586,190Z' },
    { id: 'TN', d: 'M586,190 L608,194 L632,196 L660,198 L688,196 L704,186 L722,194 L738,196 L738,218 L700,218 L640,222 L590,222 L572,224 L582,206Z' },
    { id: 'MS', d: 'M572,224 L590,222 L596,238 L596,268 L598,292 L590,316 L580,306 L570,290 L568,278 L570,258 L564,238 L550,230Z' },
    { id: 'AL', d: 'M590,222 L640,222 L656,224 L664,236 L668,268 L668,296 L664,316 L648,318 L634,316 L620,310 L610,296 L598,292 L596,268 L596,238Z' },
    { id: 'GA', d: 'M656,224 L700,218 L720,222 L734,234 L740,258 L738,290 L722,312 L706,324 L688,328 L668,320 L664,316 L668,296 L668,268 L664,236Z' },
    { id: 'FL', d: 'M668,320 L688,328 L706,324 L722,324 L740,328 L754,336 L768,352 L774,372 L770,396 L758,416 L740,430 L724,430 L714,418 L708,396 L710,372 L700,352 L688,340 L676,334Z' },
    { id: 'SC', d: 'M720,222 L740,218 L758,220 L776,230 L776,248 L760,262 L740,268 L722,270 L734,254 L740,240 L734,234Z' },
    { id: 'NC', d: 'M700,196 L738,196 L760,200 L790,198 L820,192 L840,196 L828,208 L808,216 L790,222 L776,230 L758,220 L740,218 L720,222 L700,218Z' },
    { id: 'VA', d: 'M692,158 L704,164 L704,186 L722,194 L738,196 L760,200 L790,198 L798,186 L786,178 L770,172 L750,168 L730,172 L710,170 L696,166Z' },
    { id: 'WV', d: 'M686,140 L700,142 L712,148 L718,158 L710,170 L696,166 L692,158 L704,164 L704,148 L698,138 L690,134Z' },
    { id: 'PA', d: 'M686,100 L720,92 L756,84 L776,80 L782,92 L780,110 L766,118 L748,124 L730,128 L712,128 L698,130 L690,128 L690,118Z' },
    { id: 'NY', d: 'M720,50 L756,42 L778,38 L800,36 L816,42 L826,56 L828,72 L818,80 L802,84 L782,92 L776,80 L756,84 L720,92 L706,86 L702,72 L710,62Z' },
    { id: 'VT', d: 'M800,36 L808,22 L814,16 L820,28 L818,42 L816,42Z' },
    { id: 'NH', d: 'M820,28 L828,18 L836,22 L838,36 L832,48 L826,56 L818,42 L820,28Z' },
    { id: 'ME', d: 'M836,4 L860,2 L870,12 L872,26 L868,40 L858,50 L848,46 L840,38 L838,36 L836,22 L828,18Z' },
    { id: 'MA', d: 'M828,72 L842,64 L856,60 L866,64 L870,72 L858,78 L842,78 L830,80Z' },
    { id: 'CT', d: 'M820,80 L842,78 L848,84 L842,92 L828,94 L818,90Z' },
    { id: 'RI', d: 'M856,78 L866,74 L870,82 L862,86 L854,84Z' },
    { id: 'NJ', d: 'M782,92 L792,96 L798,108 L794,122 L786,132 L778,126 L780,110Z' },
    { id: 'DE', d: 'M786,132 L794,128 L798,140 L792,148 L784,144Z' },
    { id: 'MD', d: 'M766,138 L784,144 L798,140 L806,148 L812,156 L798,162 L786,168 L770,172 L760,166 L748,154 L740,142 L748,138Z' }
  ];

  return (
    <div data-testid="na-map-widget">
      {/* Map */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-gray-500" />
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Site Visitors — United States</h3>
          </div>
          <span className="text-xs text-gray-400">{totalNA.toLocaleString()} US / {total.toLocaleString()} total</span>
        </div>
        <div className="p-4">
          <div className="relative rounded-lg overflow-hidden" style={{ background: '#ffffff' }}>
            <svg viewBox="0 0 960 600" className="w-full" style={{ maxHeight: '420px' }}>
              {/* White background */}
              <rect width="960" height="600" fill="#ffffff"/>

              {/* State shapes */}
              {states.map(state => (
                <path
                  key={state.id}
                  d={state.d}
                  fill={hoveredState === state.id ? '#3a3a3a' : '#2a2a2a'}
                  stroke="#ffffff"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                  onMouseEnter={() => setHoveredState(state.id)}
                  onMouseLeave={() => setHoveredState(null)}
                  style={{ cursor: 'pointer', transition: 'fill 0.15s' }}
                />
              ))}

              {/* Visitor location pins */}
              {locations.map((loc, i) => {
                const [cx, cy] = project(loc.latitude, loc.longitude);
                const ratio = loc.count / maxCount;
                const r = 4 + ratio * 14;
                return (
                  <g key={i}
                    onMouseEnter={() => setHoveredPin(i)}
                    onMouseLeave={() => setHoveredPin(null)}
                    style={{ cursor: 'pointer' }}>
                    {/* Pulse ring */}
                    <circle cx={cx} cy={cy} r={r + 6} fill="rgba(239,68,68,0.10)">
                      <animate attributeName="r" from={r + 2} to={r + 10} dur="2s" repeatCount="indefinite"/>
                      <animate attributeName="opacity" from="0.3" to="0" dur="2s" repeatCount="indefinite"/>
                    </circle>
                    {/* Pin dot */}
                    <circle cx={cx} cy={cy} r={r}
                      fill={`rgba(239,68,68,${0.5 + ratio * 0.5})`}
                      stroke="#fff" strokeWidth="2"/>
                    {/* Tooltip */}
                    {hoveredPin === i && (
                      <g>
                        <rect x={cx - 60} y={cy - 34} width="120" height="26" rx="6" fill="#1a1a1a" opacity="0.95"/>
                        <polygon points={`${cx - 5},${cy - 8} ${cx + 5},${cy - 8} ${cx},${cy - 2}`} fill="#1a1a1a" opacity="0.95"/>
                        <text x={cx} y={cy - 17} textAnchor="middle" fontSize="10" fill="#fff" fontFamily="system-ui" fontWeight="600">
                          {loc.city}{loc.region ? `, ${loc.region}` : ''} — {loc.count}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}

              {locations.length === 0 && (
                <text x="480" y="300" textAnchor="middle" fontSize="14" fill="#999" fontFamily="system-ui">
                  Visitor locations will appear as users browse the site
                </text>
              )}
            </svg>
          </div>

          {/* Region breakdown chips */}
          {Object.keys(regionCounts).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {Object.entries(regionCounts)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 12)
                .map(([region, count]) => {
                  const pct = totalNA > 0 ? ((count / totalNA) * 100).toFixed(1) : 0;
                  return (
                    <div key={region} className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 border border-gray-100 rounded-full" data-testid={`region-${region}`}>
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
                      <span className="text-[11px] font-medium text-gray-700">{region || 'Unknown'}</span>
                      <span className="text-[10px] text-gray-400">{count} ({pct}%)</span>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      {/* Session IP Tracker */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mt-4" data-testid="session-ip-tracker">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Monitor className="w-4 h-4 text-gray-500" />
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Session IP Tracker</h3>
          </div>
          <span className="text-xs text-gray-400">{sessions.length} recent sessions</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 text-[10px] font-semibold text-gray-500 uppercase">IP Address</th>
                <th className="text-left p-3 text-[10px] font-semibold text-gray-500 uppercase">Location</th>
                <th className="text-left p-3 text-[10px] font-semibold text-gray-500 uppercase">Country</th>
                <th className="text-center p-3 text-[10px] font-semibold text-gray-500 uppercase">Visits</th>
                <th className="text-center p-3 text-[10px] font-semibold text-gray-500 uppercase">Pages</th>
                <th className="text-left p-3 text-[10px] font-semibold text-gray-500 uppercase">Last Active</th>
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 ? (
                <tr><td colSpan="6" className="p-8 text-center text-sm text-gray-400">No session data available yet</td></tr>
              ) : (
                sessions.slice(0, 25).map((s, i) => (
                  <tr key={s.ip + i} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`session-row-${i}`}>
                    <td className="p-3">
                      <span className="font-mono text-xs text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">{s.ip}</span>
                    </td>
                    <td className="p-3 text-xs text-gray-700">
                      {s.city && s.region ? `${s.city}, ${s.region}` : s.city || s.region || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="p-3 text-xs text-gray-600">
                      {s.country_code ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="text-[10px] font-bold bg-gray-100 px-1 py-0.5 rounded">{s.country_code}</span>
                          <span className="text-gray-400">{s.country_name}</span>
                        </span>
                      ) : '—'}
                    </td>
                    <td className="p-3 text-center">
                      <span className="text-xs font-semibold text-gray-900">{s.visit_count}</span>
                    </td>
                    <td className="p-3 text-center">
                      <span className="text-xs text-gray-600">{s.pages_viewed}</span>
                    </td>
                    <td className="p-3 text-xs text-gray-500">
                      {s.last_visit ? new Date(s.last_visit).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── Visit Counter Widget ──────────────────────────
const VisitCounter = ({ token }) => {
  const [period, setPeriod] = useState('day');
  const [visits, setVisits] = useState(null);
  const periods = ['day', 'week', 'month', 'year'];

  useEffect(() => {
    axios.get(`${API}/admin/visits?period=${period}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setVisits(r.data))
      .catch(() => {});
  }, [period, token]);

  return (
    <div className="bg-white border border-gray-200 rounded p-5" data-testid="visit-counter">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-600" />
          <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Site Visits</h3>
        </div>
        <div className="flex bg-gray-100 rounded p-0.5">
          {periods.map((p) => (
            <button key={p} onClick={() => setPeriod(p)}
              className={`px-3 py-1 text-xs font-bold uppercase rounded transition-colors ${
                period === p ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-800'
              }`}
              data-testid={`visit-period-${p}`}>
              {p}
            </button>
          ))}
        </div>
      </div>
      <p className="text-4xl font-black text-gray-900" data-testid="visit-count">
        {visits ? visits.total.toLocaleString() : '—'}
      </p>
      <p className="text-xs text-gray-500 mt-1">
        {period === 'day' ? 'Today' : period === 'week' ? 'This week' : period === 'month' ? 'This month' : 'This year'}
      </p>
      {visits?.daily?.length > 1 && (
        <div className="flex items-end gap-[2px] h-10 mt-3">
          {visits.daily.map((d, i) => {
            const max = Math.max(...visits.daily.map(x => x.count));
            const h = max > 0 ? (d.count / max) * 100 : 0;
            return <div key={i} className="flex-1 bg-blue-200 rounded-t min-w-[2px]" style={{ height: `${Math.max(h, 4)}%` }}
              title={`${d._id}: ${d.count} visits`} />;
          })}
        </div>
      )}
    </div>
  );
};

// ─── Excel Import Sub-component ──────────────────────────
const ExcelImportSection = ({ token, onComplete }) => {
  const [excelFile, setExcelFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importJob, setImportJob] = useState(null);
  const [polling, setPolling] = useState(false);
  const excelInputRef = useRef(null);

  const handleExcelUpload = async () => {
    if (!excelFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      const res = await axios.post(`${API}/admin/import-excel`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Import started! Processing your file...');
      setImportJob({ id: res.data.job_id, status: 'processing', processed: 0, total_rows: 0 });
      setPolling(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start import');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (!polling || !importJob?.id) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/admin/import-status/${importJob.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setImportJob(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          setPolling(false);
          if (res.data.status === 'completed') {
            toast.success(`Import complete! ${res.data.imported} products imported.`);
            onComplete();
          } else {
            toast.error('Import failed: ' + (res.data.error_message || 'Unknown error'));
          }
        }
      } catch { /* ignore */ }
    }, 2000);
    return () => clearInterval(interval);
  }, [polling, importJob?.id, token, onComplete]);

  const progress = importJob?.total_rows > 0 ? Math.round((importJob.processed / importJob.total_rows) * 100) : 0;

  return (
    <div className="border-2 border-dashed border-gray-300 bg-gray-50 p-6 rounded" data-testid="excel-import-section">
      <h3 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
        <Upload className="w-5 h-5 text-orange-500" />
        Excel Inventory Import
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Upload .xlsx with columns: SKU, Item ID, Product Name, Price, Currency, Item Page URL, Primary Image URL
      </p>
      {!importJob || importJob.status === 'completed' || importJob.status === 'failed' ? (
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <input ref={excelInputRef} type="file" accept=".xlsx,.xls"
              onChange={(e) => setExcelFile(e.target.files[0])}
              className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:font-semibold file:bg-gray-900 file:text-white hover:file:bg-gray-700 file:cursor-pointer file:rounded"
              data-testid="excel-file-input" />
          </div>
          <button onClick={handleExcelUpload} disabled={uploading || !excelFile}
            className="px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 disabled:opacity-40 flex items-center gap-2"
            data-testid="excel-import-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Uploading...' : 'Import'}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="font-medium text-gray-700">Processing...</span>
              <span className="text-gray-500">{importJob.processed?.toLocaleString()} / {importJob.total_rows?.toLocaleString()} ({progress}%)</span>
            </div>
            <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-gray-900 transition-all duration-500 rounded-full" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-white border border-gray-200 p-2 rounded"><p className="text-lg font-bold text-green-600">{importJob.imported?.toLocaleString()}</p><p className="text-xs text-gray-500">Imported</p></div>
            <div className="bg-white border border-gray-200 p-2 rounded"><p className="text-lg font-bold text-yellow-600">{importJob.skipped?.toLocaleString()}</p><p className="text-xs text-gray-500">Skipped</p></div>
            <div className="bg-white border border-gray-200 p-2 rounded"><p className="text-lg font-bold text-red-600">{importJob.errors?.toLocaleString()}</p><p className="text-xs text-gray-500">Errors</p></div>
          </div>
          {importJob.category_counts && Object.keys(importJob.category_counts).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(importJob.category_counts).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                <span key={cat} className="px-2 py-0.5 bg-white border border-gray-200 text-xs text-gray-700 rounded capitalize">{cat}: {count.toLocaleString()}</span>
              ))}
            </div>
          )}
        </div>
      )}
      {importJob?.status === 'completed' && (
        <div className="mt-3 p-3 bg-green-50 border border-green-200 text-green-800 rounded text-sm">
          Import complete: {importJob.imported?.toLocaleString()} imported, {importJob.skipped?.toLocaleString()} skipped, {importJob.errors?.toLocaleString()} errors.
        </div>
      )}
      {importJob?.status === 'failed' && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 text-red-800 rounded text-sm">{importJob.error_message}</div>
      )}
    </div>
  );
};

// ─── Main Admin Page ──────────────────────────
const AdminPage = () => {
  const { user, token, isAdmin, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  // Prevent spacebar from scrolling the page when not in an input
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === ' ' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName) && !e.target.isContentEditable) {
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Product modals
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);

  // Bulk import
  const [bulkImportData, setBulkImportData] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);

  // Promotions
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [promoSubTab, setPromoSubTab] = useState('promotions');
  const [editingPromo, setEditingPromo] = useState(null);
  const [promoSaving, setPromoSaving] = useState(false);

  // Quick add
  const [quickAddForm, setQuickAddForm] = useState({ title: '', description: '', category: 'art', subcategory: '', tags: '' });
  const [quickAddImage, setQuickAddImage] = useState(null);
  const [quickAddPreview, setQuickAddPreview] = useState('');
  const [quickAddSaving, setQuickAddSaving] = useState(false);
  const fileInputRef = useRef(null);

  const [promoForm, setPromoForm] = useState({ name: '', description: '', discount_type: 'percentage', discount_value: '', applies_to: 'all', category: '', is_active: true, bogo_buy: '1', bogo_get: '1' });

  // User management
  const [showUserModal, setShowUserModal] = useState(false);
  const [userSaving, setUserSaving] = useState(false);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'viewer' });
  const [showPassword, setShowPassword] = useState(false);

  // Product form
  const [productForm, setProductForm] = useState({ title: '', description: '', category: 'art', subcategory: '', sku: '', price: '', image_url: '', artist: '', stock: '100', featured: false, trending: false, tags: '' });

  // Products sub-tab & manage state
  const [productSubTab, setProductSubTab] = useState('manage');
  const [productSearch, setProductSearch] = useState('');
  const [productPage, setProductPage] = useState(1);
  const [productTotal, setProductTotal] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [managedProducts, setManagedProducts] = useState([]);
  const [managedLoading, setManagedLoading] = useState(false);
  const [productCategoryFilter, setProductCategoryFilter] = useState('');
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [duplicateMode, setDuplicateMode] = useState('title');
  const [duplicates, setDuplicates] = useState(null);
  const [dupLoading, setDupLoading] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterDropdownRef = React.useRef(null);
  const PRODUCTS_PER_PAGE = 25;

  // Close filter dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
        setShowFilterDropdown(false);
      }
    };
    if (showFilterDropdown) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showFilterDropdown]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) { navigate('/'); return; }
    fetchData();
  }, [isAdmin, navigate, authLoading]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, ordersRes, promosRes, catsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/promotions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/categories`),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data);
      setPromotions(promosRes.data);
      setCategories(catsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch managed products with server-side pagination
  const fetchManagedProducts = useCallback(async () => {
    setManagedLoading(true);
    try {
      const skip = (productPage - 1) * PRODUCTS_PER_PAGE;
      const params = new URLSearchParams({ limit: PRODUCTS_PER_PAGE, skip });
      if (productSearch) params.set('search', productSearch);
      if (productCategoryFilter) params.set('category', productCategoryFilter);

      const countParams = new URLSearchParams();
      if (productSearch) countParams.set('search', productSearch);
      if (productCategoryFilter) countParams.set('category', productCategoryFilter);

      const [prodRes, countRes] = await Promise.all([
        axios.get(`${API}/products?${params}`),
        axios.get(`${API}/products/count?${countParams}`)
      ]);
      setManagedProducts(prodRes.data);
      setProductTotal(countRes.data.count);
    } catch {
      toast.error('Failed to load products');
    } finally {
      setManagedLoading(false);
    }
  }, [productPage, productSearch, productCategoryFilter]);

  useEffect(() => {
    if (activeTab === 'products' && productSubTab === 'manage') {
      if (showDuplicates) {
        fetchDuplicates(duplicateMode);
      } else {
        fetchManagedProducts();
      }
    }
  }, [activeTab, productSubTab, fetchManagedProducts, showDuplicates, duplicateMode]);

  const fetchDuplicates = async (mode) => {
    setDupLoading(true);
    try {
      const res = await axios.get(`${API}/admin/products/duplicates?mode=${mode}`, { headers: { Authorization: `Bearer ${token}` } });
      setDuplicates(res.data);
    } catch { toast.error('Failed to load duplicates'); }
    finally { setDupLoading(false); }
  };

  // ─── Handlers ──────────────────────────
  const resetUserForm = () => { setUserForm({ name: '', email: '', password: '', role: 'viewer' }); setShowPassword(false); };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!userForm.name || !userForm.email || !userForm.password) { toast.error('Fill in all fields'); return; }
    setUserSaving(true);
    try {
      await axios.post(`${API}/admin/users/staff`, userForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('User created!'); setShowUserModal(false); resetUserForm(); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create user'); }
    finally { setUserSaving(false); }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try { await axios.put(`${API}/admin/users/${userId}`, { role: newRole }, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Role updated!'); fetchData(); }
    catch { toast.error('Failed to update user'); }
  };

  const handleToggleUserActive = async (userId, isActive) => {
    try { await axios.put(`${API}/admin/users/${userId}`, { is_active: !isActive }, { headers: { Authorization: `Bearer ${token}` } }); toast.success(`User ${isActive ? 'disabled' : 'enabled'}!`); fetchData(); }
    catch { toast.error('Failed to update user'); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try { await axios.delete(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('User deleted!'); fetchData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete user'); }
  };

  const handleToggleTrending = async (productId, currentTrending) => {
    try {
      await axios.put(`${API}/admin/products/${productId}/trending?trending=${!currentTrending}&trending_order=0`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(currentTrending ? 'Removed from trending' : 'Added to trending!');
      fetchManagedProducts();
    } catch { toast.error('Failed to update trending'); }
  };

  const handleUpdateCategory = async (productId, newCategory) => {
    try {
      await axios.put(`${API}/admin/products/${productId}`, { category: newCategory, subcategory: null }, { headers: { Authorization: `Bearer ${token}` } });
      setManagedProducts(prev => prev.map(p => p.id === productId ? { ...p, category: newCategory, subcategory: null } : p));
      toast.success('Category updated!');
    } catch { toast.error('Failed to update category'); }
  };

  const handleUpdateSubcategory = async (productId, newSubcategory) => {
    try {
      await axios.put(`${API}/admin/products/${productId}`, { subcategory: newSubcategory || null }, { headers: { Authorization: `Bearer ${token}` } });
      setManagedProducts(prev => prev.map(p => p.id === productId ? { ...p, subcategory: newSubcategory || null } : p));
      toast.success('Subcategory updated!');
    } catch { toast.error('Failed to update subcategory'); }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) { setQuickAddImage(file); const reader = new FileReader(); reader.onloadend = () => setQuickAddPreview(reader.result); reader.readAsDataURL(file); }
  };

  const resetQuickAddForm = () => { setQuickAddForm({ title: '', description: '', category: 'art', subcategory: '', tags: '' }); setQuickAddImage(null); setQuickAddPreview(''); if (fileInputRef.current) fileInputRef.current.value = ''; };

  const handleQuickAddSubmit = async (e) => {
    e.preventDefault();
    if (!quickAddForm.title || !quickAddImage) { toast.error('Provide a title and image'); return; }
    setQuickAddSaving(true);
    try {
      const formData = new FormData();
      formData.append('title', quickAddForm.title); formData.append('description', quickAddForm.description);
      formData.append('category', quickAddForm.category); formData.append('tags', quickAddForm.tags); formData.append('file', quickAddImage);
      await axios.post(`${API}/admin/products/quick-add`, formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      toast.success('Product added!'); resetQuickAddForm(); fetchManagedProducts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add'); }
    finally { setQuickAddSaving(false); }
  };

  const resetPromoForm = () => setPromoForm({ name: '', description: '', discount_type: 'percentage', discount_value: '', applies_to: 'all', category: '', is_active: true, bogo_buy: '1', bogo_get: '1' });

  const handlePromoSubmit = async (e) => {
    e.preventDefault();
    if (!promoForm.name || (promoForm.discount_type !== 'bogo' && !promoForm.discount_value)) { toast.error('Fill required fields'); return; }
    setPromoSaving(true);
    try {
      const promoData = { ...promoForm, discount_value: parseFloat(promoForm.discount_value || 0), bogo_buy: parseInt(promoForm.bogo_buy || 0), bogo_get: parseInt(promoForm.bogo_get || 0), product_ids: [] };
      if (editingPromo) { await axios.put(`${API}/admin/promotions/${editingPromo.id}`, promoData, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Promotion updated!'); }
      else { await axios.post(`${API}/admin/promotions`, promoData, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Promotion created!'); }
      setShowPromoModal(false); setEditingPromo(null); resetPromoForm(); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save'); }
    finally { setPromoSaving(false); }
  };

  const handleEditPromo = (promo) => {
    setEditingPromo(promo);
    setPromoForm({ name: promo.name, description: promo.description || '', discount_type: promo.discount_type, discount_value: promo.discount_value.toString(), applies_to: promo.applies_to, category: promo.category || '', is_active: promo.is_active, bogo_buy: String(promo.bogo_buy || 1), bogo_get: String(promo.bogo_get || 1) });
    setShowPromoModal(true);
  };

  const handleDeletePromo = async (promoId) => {
    if (!window.confirm('Delete this promotion?')) return;
    try { await axios.delete(`${API}/admin/promotions/${promoId}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Deleted!'); fetchData(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleTogglePromo = async (promo) => {
    try {
      if (promo.is_active) { await axios.post(`${API}/admin/promotions/${promo.id}/remove`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Deactivated'); }
      else { await axios.post(`${API}/admin/promotions/${promo.id}/apply`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Activated'); }
      fetchData();
    } catch { toast.error('Failed to toggle'); }
  };

  const handleClearAllPromotions = async () => {
    if (!window.confirm('Clear ALL discounts from ALL products?')) return;
    try { await axios.post(`${API}/admin/promotions/clear-all`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success('All discounts cleared!'); fetchData(); }
    catch { toast.error('Failed to clear'); }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const productData = { title: productForm.title, description: productForm.description, category: productForm.category, subcategory: productForm.subcategory || null, sku: productForm.sku || null, price: 11.99, image_url: productForm.image_url, artist: productForm.artist || null, stock: parseInt(productForm.stock), featured: productForm.featured, trending: productForm.trending, tags: productForm.tags.split(',').map(t => t.trim()).filter(Boolean), sizes: DEFAULT_SIZES };
      if (editingProduct) { await axios.put(`${API}/admin/products/${editingProduct.id}`, productData, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Updated!'); }
      else { await axios.post(`${API}/admin/products`, productData, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Created!'); }
      setShowProductModal(false); setEditingProduct(null); resetProductForm(); fetchManagedProducts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({ title: product.title, description: product.description, category: product.category, subcategory: product.subcategory || '', sku: product.sku || '', price: product.price.toString(), image_url: product.image_url, artist: product.artist || '', stock: product.stock.toString(), featured: product.featured, trending: product.trending || false, tags: product.tags?.join(', ') || '' });
    setShowProductModal(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    try { await axios.delete(`${API}/admin/products/${productId}`, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Deleted!'); fetchManagedProducts(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleBulkDeleteSelected = async () => {
    if (selectedProducts.size === 0) return;
    if (!window.confirm(`Delete ${selectedProducts.size} selected products?`)) return;
    try {
      await Promise.all([...selectedProducts].map(id => axios.delete(`${API}/admin/products/${id}`, { headers: { Authorization: `Bearer ${token}` } })));
      toast.success(`${selectedProducts.size} products deleted!`);
      setSelectedProducts(new Set());
      if (showDuplicates) fetchDuplicates();
      else fetchManagedProducts();
    } catch { toast.error('Some deletions failed'); if (showDuplicates) fetchDuplicates(); else fetchManagedProducts(); }
  };

  const handleDeleteDuplicates = async (ids) => {
    const keepId = ids[0];
    const deleteIds = ids.slice(1);
    if (!window.confirm(`Keep 1 copy and delete ${deleteIds.length} duplicate(s)?`)) return;
    try {
      await Promise.all(deleteIds.map(id => axios.delete(`${API}/admin/products/${id}`, { headers: { Authorization: `Bearer ${token}` } })));
      toast.success(`Removed ${deleteIds.length} duplicates!`);
      fetchDuplicates();
    } catch { toast.error('Some deletions failed'); fetchDuplicates(); }
  };

  const handleBulkCategoryUpdate = async (newCategory) => {
    if (selectedProducts.size === 0) return;
    const catName = categories.find(c => c.id === newCategory)?.name || newCategory;
    if (!window.confirm(`Move ${selectedProducts.size} products to "${catName}"?`)) return;
    try {
      await Promise.all([...selectedProducts].map(id => axios.put(`${API}/admin/products/${id}`, { category: newCategory }, { headers: { Authorization: `Bearer ${token}` } })));
      toast.success(`${selectedProducts.size} products moved to ${catName}!`);
      setSelectedProducts(new Set());
      fetchManagedProducts();
    } catch { toast.error('Some updates failed'); fetchManagedProducts(); }
  };

  const toggleProductSelect = (id) => {
    setSelectedProducts(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };

  const toggleSelectAll = () => {
    if (selectedProducts.size === managedProducts.length) setSelectedProducts(new Set());
    else setSelectedProducts(new Set(managedProducts.map(p => p.id)));
  };

  const handleBulkImport = async () => {
    if (!bulkImportData.trim()) { toast.error('Enter import data'); return; }
    setBulkImporting(true);
    try {
      let items = [];
      try { items = JSON.parse(bulkImportData); } catch {
        items = bulkImportData.trim().split('\n').map(line => {
          const parts = line.split(',').map(p => p.trim());
          return { title: parts[0] || 'Untitled', description: parts[1] || '', category: parts[2] || 'art', image_url: parts[3] || '', tags: parts[4] ? parts[4].split('|') : [] };
        }).filter(item => item.image_url);
      }
      if (!items.length) { toast.error('No valid items'); return; }
      const response = await axios.post(`${API}/admin/quick-import`, items, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Imported ${response.data.imported} products!`); setBulkImportData(''); fetchManagedProducts();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to import'); }
    finally { setBulkImporting(false); }
  };

  const handleUpdateAllSizes = async () => {
    if (!window.confirm('Update all products to new size/pricing?')) return;
    try { const response = await axios.put(`${API}/admin/update-all-sizes`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success(`Updated ${response.data.updated} products!`); }
    catch { toast.error('Failed to update sizes'); }
  };

  const handleOrderStatusUpdate = async (orderId, status) => {
    try { await axios.put(`${API}/admin/orders/${orderId}/status?status=${status}`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Status updated!'); fetchData(); }
    catch { toast.error('Failed to update order'); }
  };

  const handleTrackingUpdate = async (orderId, trackingNumber) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/tracking`, { tracking_number: trackingNumber }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Tracking number saved!');
      fetchData();
    } catch { toast.error('Failed to update tracking'); }
  };

  const handleSingleStatusUpdate = async (orderId, newStatus) => {
    try {
      const res = await axios.put(`${API}/admin/orders/${orderId}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Order updated to ${newStatus}${res.data.email_sent ? ' — notification email sent!' : ''}`);
      fetchData();
    } catch { toast.error('Failed to update status'); }
  };

  const handleCancelOrder = async (orderId) => {
    const reason = window.prompt('Enter cancellation reason (optional):');
    if (reason === null) return; // user pressed Cancel on prompt
    try {
      await axios.post(`${API}/admin/orders/${orderId}/cancel`, { reason }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Order cancelled!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel order');
    }
  };

  // ─── Multi-select & Bulk Actions ───
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [editingOrder, setEditingOrder] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [abandonedCarts, setAbandonedCarts] = useState({ carts: [], total_count: 0, total_value: 0 });
  const [orderSubTab, setOrderSubTab] = useState('orders');
  const [recoveryModal, setRecoveryModal] = useState(null);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryDiscount, setRecoveryDiscount] = useState('');
  const [recoverySending, setRecoverySending] = useState(false);

  const handleSendRecovery = async (cart) => {
    if (!recoveryEmail.trim()) { toast.error('Please enter an email address'); return; }
    setRecoverySending(true);
    try {
      const res = await axios.post(`${API}/admin/abandoned-carts/${cart.session_id}/send-recovery`, {
        email: recoveryEmail,
        discount_code: recoveryDiscount,
        subject: 'You left something behind at PosterFire!'
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.method === 'manual') {
        // No email service - open a new window with the email HTML for copy/paste
        const w = window.open('', '_blank');
        if (w) {
          w.document.write(`<html><head><title>Recovery Email Preview</title></head><body>
            <div style="max-width:640px;margin:20px auto;font-family:Arial,sans-serif;">
              <div style="background:#f3f4f6;padding:16px;border-radius:8px;margin-bottom:20px;">
                <p style="margin:0 0 8px;"><strong>To:</strong> ${recoveryEmail}</p>
                <p style="margin:0 0 8px;"><strong>Subject:</strong> ${res.data.subject}</p>
                <p style="margin:0;color:#666;font-size:13px;">Copy this email and send via your email provider. To enable auto-sending, add RESEND_API_KEY to backend/.env</p>
              </div>
              <div style="border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">${res.data.html_body}</div>
            </div>
          </body></html>`);
        }
        toast.success('Email template generated — copy and send manually');
      } else {
        toast.success(`Recovery email sent to ${recoveryEmail}`);
      }
      setRecoveryModal(null);
      setRecoveryEmail('');
      setRecoveryDiscount('');
      fetchAbandonedCarts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send recovery email');
    } finally {
      setRecoverySending(false);
    }
  };

  const toggleOrderSelect = (id) => {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllOrders = () => {
    if (selectedOrders.size === orders.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(orders.map(o => o.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedOrders.size} orders? This cannot be undone.`)) return;
    try {
      await axios.post(`${API}/admin/orders/bulk-delete`, { order_ids: Array.from(selectedOrders) }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Deleted ${selectedOrders.size} orders`);
      setSelectedOrders(new Set());
      fetchData();
    } catch { toast.error('Failed to delete orders'); }
  };

  const handleBulkStatus = async (status) => {
    try {
      await axios.post(`${API}/admin/orders/bulk-status`, { order_ids: Array.from(selectedOrders), status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Updated ${selectedOrders.size} orders to ${status}`);
      setSelectedOrders(new Set());
      fetchData();
    } catch { toast.error('Failed to update orders'); }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Delete this order? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Order deleted');
      fetchData();
    } catch { toast.error('Failed to delete order'); }
  };

  const openEditOrder = (order) => {
    setEditingOrder(order.id);
    setEditForm({
      status: order.status,
      tracking_number: order.tracking_number || '',
      notes: order.notes || '',
      items: order.items?.map(i => ({ ...i })) || [],
      shipping_address: { ...(order.shipping_address || {}) }
    });
  };

  const handleSaveOrder = async () => {
    try {
      await axios.put(`${API}/admin/orders/${editingOrder}`, editForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Order updated!');
      setEditingOrder(null);
      fetchData();
    } catch { toast.error('Failed to update order'); }
  };

  const fetchAbandonedCarts = async () => {
    try {
      const res = await axios.get(`${API}/admin/abandoned-carts`, { headers: { Authorization: `Bearer ${token}` } });
      setAbandonedCarts(res.data);
    } catch {}
  };

  useEffect(() => {
    if (activeTab === 'orders' && orderSubTab === 'abandoned') fetchAbandonedCarts();
  }, [activeTab, orderSubTab]);

  const resetProductForm = () => setProductForm({ title: '', description: '', category: 'art', subcategory: '', sku: '', price: '', image_url: '', artist: '', stock: '100', featured: false, trending: false, tags: '' });

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'pricing', label: 'Pricing', icon: CircleDollarSign },
    { id: 'marketing', label: 'Marketing', icon: Megaphone },
    { id: 'blog', label: 'Blog', icon: FileText },
    { id: 'earnings', label: 'Earnings', icon: TrendingUp },
    { id: 'orders', label: 'Orders', icon: ShoppingCart },
    { id: 'promotions', label: 'Promotions', icon: Tag },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'import', label: 'Bulk Import', icon: Upload },
    { id: 'seo', label: 'SEO', icon: Globe },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'categories', label: 'Categories', icon: LayoutList },
    { id: 'snapshots', label: 'Snapshots', icon: Download },
    { id: 'construction', label: 'Construction', icon: Construction },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  // Inventory role: only show product tabs
  const userRole = user?.role || 'admin';
  const INVENTORY_TABS = ['products', 'import'];
  const visibleNavItems = userRole === 'inventory' ? navItems.filter(t => INVENTORY_TABS.includes(t.id)) : navItems;

  const productTotalPages = Math.ceil(productTotal / PRODUCTS_PER_PAGE);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;
  if (!isAdmin) return null;

  return (
    <div className="min-h-screen flex bg-gray-100 text-gray-900" data-testid="admin-page">
      {/* ─── Sidebar ─── */}
      <aside className={`fixed lg:static lg:relative inset-y-0 left-0 z-50 w-60 bg-gray-950 transform transition-transform lg:transform-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-5 border-b border-gray-800">
          <Link to="/" className="block">
            <h1 className="font-heading font-black text-xl tracking-tighter text-white">
              POSTER<span className="text-orange-500">FIRE</span>
            </h1>
          </Link>
          <p className="text-[10px] text-gray-500 mt-0.5 uppercase tracking-[0.2em]">Admin Panel</p>
        </div>
        <nav className="p-3 space-y-0.5">
          {visibleNavItems.map((item) => (
            <button key={item.id} onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              className={`w-full text-left flex items-center gap-3 py-2.5 px-3 rounded text-sm font-bold transition-colors ${
                activeTab === item.id ? 'bg-white text-gray-900' : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
              data-testid={`admin-nav-${item.id}`}>
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-800">
          <div className="px-3 py-2 mb-1">
            <p className="text-sm font-medium text-white">{user?.name}</p>
            <p className="text-xs text-gray-500">{user?.email}</p>
          </div>
          <button onClick={() => { logout(); navigate('/'); }}
            className="w-full text-left flex items-center gap-3 py-2.5 px-3 rounded text-sm text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
            data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* ─── Main Content ─── */}
      <main className="flex-1 min-h-screen">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 lg:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-1.5 rounded hover:bg-gray-100" data-testid="admin-menu-toggle">
              <Menu className="w-5 h-5 text-gray-700" />
            </button>
            <h2 className="text-lg font-semibold text-gray-900 capitalize">{activeTab}</h2>
          </div>
        </header>

        <div className="p-4 lg:p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
          ) : (
            <>
              {/* ─── Dashboard ─── */}
              {activeTab === 'dashboard' && stats && (
                <div className="space-y-6" data-testid="admin-dashboard">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { label: 'Revenue', value: `$${stats.total_revenue.toFixed(2)}`, icon: DollarSign, color: 'text-green-600 bg-green-50' },
                      { label: 'Orders', value: stats.total_orders, icon: ShoppingCart, color: 'text-blue-600 bg-blue-50' },
                      { label: 'Products', value: stats.total_products.toLocaleString(), icon: Package, color: 'text-purple-600 bg-purple-50' },
                      { label: 'Users', value: stats.total_users, icon: Users, color: 'text-gray-700 bg-gray-100' },
                    ].map(s => (
                      <div key={s.label} className="bg-white border border-gray-200 rounded p-5">
                        <div className="flex items-center gap-3">
                          <div className={`p-2.5 rounded ${s.color}`}><s.icon className="w-5 h-5" /></div>
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wider">{s.label}</p>
                            <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {stats.pending_orders > 0 && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                      <strong>{stats.pending_orders}</strong> orders pending review
                    </div>
                  )}
                  <VisitCounter token={token} />
                  <WorldMapWidget token={token} />
                </div>
              )}

              {/* ─── Products ─── */}
              {activeTab === 'products' && (
                <div className="space-y-4" data-testid="admin-products">
                  {/* Sub-tabs */}
                  <div className="flex gap-1 bg-gray-200 p-1 rounded w-fit" data-testid="product-subtabs">
                    <button onClick={() => { setProductSubTab('manage'); setSelectedProducts(new Set()); }}
                      className={`px-4 py-2 text-sm font-medium rounded transition-colors ${productSubTab === 'manage' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                      data-testid="subtab-manage">
                      Manage Products
                    </button>
                    <button onClick={() => setProductSubTab('add')}
                      className={`px-4 py-2 text-sm font-medium rounded transition-colors ${productSubTab === 'add' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                      data-testid="subtab-add">
                      Add Product
                    </button>
                    <button onClick={() => setProductSubTab('ai-upload')}
                      className={`px-4 py-2 text-sm font-medium rounded transition-colors ${productSubTab === 'ai-upload' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                      data-testid="subtab-ai-upload">
                      AI Upload
                    </button>
                    <button onClick={() => setProductSubTab('ai-categorize')}
                      className={`px-4 py-2 text-sm font-medium rounded transition-colors ${productSubTab === 'ai-categorize' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                      data-testid="subtab-ai-categorize">
                      AI Categorize
                    </button>
                  </div>

                  {/* ── Manage Products ── */}
                  {productSubTab === 'manage' && (
                    <div className="space-y-3">
                      {/* Toolbar */}
                      <div className="bg-white border border-gray-200 rounded p-3 flex flex-wrap items-center gap-3">
                        {/* Search */}
                        <div className="relative flex-1 min-w-[200px]">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text" placeholder="Search products..."
                            value={productSearch}
                            onChange={(e) => { setProductSearch(e.target.value); setProductPage(1); setSelectedProducts(new Set()); }}
                            className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            data-testid="product-search-input"
                          />
                          {productSearch && (
                            <button onClick={() => { setProductSearch(''); setProductPage(1); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        {/* Category Filter */}
                        <select
                          value={productCategoryFilter}
                          onChange={(e) => { setProductCategoryFilter(e.target.value); setProductPage(1); setSelectedProducts(new Set()); }}
                          className="border border-gray-200 rounded px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
                          data-testid="product-category-filter"
                        >
                          <option value="">All Categories</option>
                          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        {/* Duplicates Toggle */}
                        <div className="relative" ref={filterDropdownRef}>
                          <button onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                            className={`px-3 py-2 text-sm font-medium rounded border flex items-center gap-1.5 transition-colors ${
                              showDuplicates ? 'bg-orange-50 text-orange-700 border-orange-300' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                            }`}
                            data-testid="filter-dropdown-btn">
                            {showDuplicates ? (
                              <>{duplicateMode === 'title' ? <Copy className="w-3.5 h-3.5" /> : duplicateMode === 'photo' ? <ImagePlus className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                              {duplicateMode === 'title' ? 'Same Title' : duplicateMode === 'photo' ? 'Same Photo' : 'Duplicates'}</>
                            ) : (
                              <><Search className="w-3.5 h-3.5" />Find Duplicates</>
                            )}
                            <ChevronRight className={`w-3 h-3 transition-transform ${showFilterDropdown ? 'rotate-90' : ''}`} />
                          </button>
                          {showFilterDropdown && (
                            <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1" data-testid="filter-dropdown-menu">
                              <button onClick={() => { setDuplicateMode('title'); setShowDuplicates(true); setShowFilterDropdown(false); setSelectedProducts(new Set()); }}
                                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 hover:bg-gray-50 ${showDuplicates && duplicateMode === 'title' ? 'bg-orange-50 text-orange-700' : 'text-gray-700'}`}
                                data-testid="filter-same-title">
                                <Copy className="w-4 h-4 flex-shrink-0" />
                                <div>
                                  <p className="font-medium">Same Title</p>
                                  <p className="text-xs text-gray-400">Products with identical titles</p>
                                </div>
                              </button>
                              <button onClick={() => { setDuplicateMode('photo'); setShowDuplicates(true); setShowFilterDropdown(false); setSelectedProducts(new Set()); }}
                                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 hover:bg-gray-50 ${showDuplicates && duplicateMode === 'photo' ? 'bg-orange-50 text-orange-700' : 'text-gray-700'}`}
                                data-testid="filter-same-photo">
                                <ImagePlus className="w-4 h-4 flex-shrink-0" />
                                <div>
                                  <p className="font-medium">Same Photo</p>
                                  <p className="text-xs text-gray-400">Products sharing the same image</p>
                                </div>
                              </button>
                              <div className="border-t border-gray-100 my-1" />
                              {showDuplicates && (
                                <button onClick={() => { setShowDuplicates(false); setShowFilterDropdown(false); setSelectedProducts(new Set()); }}
                                  className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 hover:bg-gray-50 text-gray-500"
                                  data-testid="filter-clear">
                                  <X className="w-4 h-4 flex-shrink-0" />
                                  <p className="font-medium">Clear Filter</p>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        {/* Bulk actions */}
                        {selectedProducts.size > 0 && (
                          <div className="flex items-center gap-2">
                            <select
                              onChange={(e) => { if (e.target.value) handleBulkCategoryUpdate(e.target.value); e.target.value = ''; }}
                              defaultValue=""
                              className="border border-gray-200 rounded px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
                              data-testid="bulk-category-select"
                            >
                              <option value="" disabled>Move to category...</option>
                              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                            <button onClick={handleBulkDeleteSelected}
                              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 flex items-center gap-2"
                              data-testid="bulk-delete-btn">
                              <Trash2 className="w-4 h-4" />
                              Delete {selectedProducts.size}
                            </button>
                          </div>
                        )}
                        {/* Export CSV */}
                        <button
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `${API}/admin/products/export`;
                            link.setAttribute('download', 'posterfire_inventory.csv');
                            // Need auth header - use fetch instead
                            fetch(`${API}/admin/products/export`, { headers: { Authorization: `Bearer ${token}` } })
                              .then(res => res.blob())
                              .then(blob => {
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = 'posterfire_inventory.csv';
                                a.click();
                                URL.revokeObjectURL(url);
                                toast.success('Inventory exported!');
                              })
                              .catch(() => toast.error('Failed to export'));
                          }}
                          className="px-3 py-2 text-sm font-medium rounded border border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
                          data-testid="export-inventory-btn"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Export CSV
                        </button>
                        <button
                          onClick={() => {
                            fetch(`${API}/admin/products/export/gmc`, { headers: { Authorization: `Bearer ${token}` } })
                              .then(res => res.blob())
                              .then(blob => {
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = 'posterfire_gmc_feed.xml';
                                a.click();
                                URL.revokeObjectURL(url);
                                toast.success('Google Merchant Center XML exported!');
                              })
                              .catch(() => toast.error('Failed to export GMC feed'));
                          }}
                          className="px-3 py-2 text-sm font-medium rounded border border-blue-200 text-blue-600 hover:bg-blue-50 flex items-center gap-1.5 transition-colors"
                          data-testid="export-gmc-btn"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Export GMC XML
                        </button>
                        <span className="text-sm text-gray-500 ml-auto" data-testid="product-total-count">
                          {productTotal.toLocaleString()} products
                        </span>
                      </div>

                      {/* Duplicates View */}
                      {showDuplicates ? (
                        <div className="bg-white border border-gray-200 rounded overflow-hidden" data-testid="duplicates-view">
                          {dupLoading ? (
                            <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
                          ) : !duplicates || duplicates.groups.length === 0 ? (
                            <div className="p-10 text-center">
                              <Copy className="w-10 h-10 mx-auto text-green-300 mb-3" />
                              <h3 className="text-lg font-bold text-gray-900 mb-1">No Duplicates Found</h3>
                              <p className="text-sm text-gray-500">No products with the same {duplicateMode === 'photo' ? 'photo' : 'title'} found.</p>
                            </div>
                          ) : (
                            <>
                              <div className="px-4 py-3 bg-orange-50 border-b border-orange-200 flex items-center justify-between">
                                <p className="text-sm text-orange-800">
                                  {duplicateMode === 'photo' ? <ImagePlus className="w-4 h-4 inline mr-1.5" /> : <Copy className="w-4 h-4 inline mr-1.5" />}
                                  Found <strong>{duplicates.total_duplicate_groups}</strong> groups with <strong>{duplicates.total_extra_copies}</strong> extra copies
                                  <span className="ml-2 text-xs opacity-70">(by {duplicateMode === 'photo' ? 'image' : 'title'})</span>
                                </p>
                              </div>
                              <div className="divide-y divide-gray-100">
                                {duplicates.groups.map((group, gi) => (
                                  <div key={gi} className="p-4 hover:bg-gray-50" data-testid={`duplicate-group-${gi}`}>
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        {group.image_url && <img src={group.image_url} alt="" className="w-10 h-14 object-cover rounded bg-gray-100" />}
                                        <div>
                                          <p className="text-sm font-medium text-gray-900">{group.title}</p>
                                          <p className="text-xs text-gray-500 capitalize">{group.category} — {group.count} copies</p>
                                        </div>
                                      </div>
                                      <button onClick={() => handleDeleteDuplicates(group.ids)}
                                        className="px-3 py-1.5 bg-red-50 text-red-600 text-xs font-semibold rounded hover:bg-red-100 flex items-center gap-1.5"
                                        data-testid={`delete-dupes-${gi}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                        Remove {group.count - 1} duplicate{group.count - 1 > 1 ? 's' : ''}
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                      /* Regular Table */
                      <div className="bg-white border border-gray-200 rounded overflow-hidden">
                        {managedLoading ? (
                          <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
                        ) : (
                          <table className="w-full">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="w-10 p-3">
                                  <button onClick={toggleSelectAll} className="text-gray-500 hover:text-gray-900" data-testid="select-all-products">
                                    {selectedProducts.size === managedProducts.length && managedProducts.length > 0 ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                                  </button>
                                </th>
                                <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Product</th>
                                <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                                <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Subcategory</th>
                                <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Price</th>
                                <th className="text-center p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Trending</th>
                                <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Stock</th>
                                <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {managedProducts.map((product) => (
                                <tr key={product.id} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${selectedProducts.has(product.id) ? 'bg-blue-50' : ''}`}
                                  data-testid={`product-row-${product.id}`}>
                                  <td className="p-3">
                                    <button onClick={() => toggleProductSelect(product.id)} className="text-gray-400 hover:text-gray-700" data-testid={`select-product-${product.id}`}>
                                      {selectedProducts.has(product.id) ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4" />}
                                    </button>
                                  </td>
                                  <td className="p-3">
                                    <div className="flex items-center gap-3">
                                      <img src={product.image_url} alt="" className="w-10 h-14 object-cover rounded bg-gray-100" />
                                      <div className="min-w-0">
                                        <a href={`/product/${product.id}`} target="_blank" rel="noopener noreferrer"
                                          className="text-sm font-medium text-gray-900 truncate max-w-[300px] block hover:text-blue-600 hover:underline cursor-pointer"
                                          data-testid={`product-link-${product.id}`}>
                                          {product.title}
                                        </a>
                                        {product.featured && <span className="text-[10px] text-orange-600 font-semibold uppercase">Featured</span>}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="p-3">
                                    <select
                                      value={product.category}
                                      onChange={(e) => handleUpdateCategory(product.id, e.target.value)}
                                      className="text-sm text-gray-700 bg-transparent border border-gray-200 rounded px-2 py-1 cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent capitalize"
                                      data-testid={`category-select-${product.id}`}
                                    >
                                      {categories.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="p-3">
                                    {(() => {
                                      const subs = categories.find(c => c.id === product.category)?.subcategories || [];
                                      return subs.length > 0 ? (
                                        <select
                                          value={product.subcategory || ''}
                                          onChange={(e) => handleUpdateSubcategory(product.id, e.target.value)}
                                          className="text-xs text-gray-600 bg-transparent border border-gray-200 rounded px-2 py-1 cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent max-w-[140px]"
                                          data-testid={`subcategory-select-${product.id}`}
                                        >
                                          <option value="">None</option>
                                          {subs.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                        </select>
                                      ) : (
                                        <span className="text-xs text-gray-300">—</span>
                                      );
                                    })()}
                                  </td>
                                  <td className="p-3"><span className="text-sm font-semibold text-gray-900">${product.price.toFixed(2)}</span></td>
                                  <td className="p-3 text-center">
                                    <button onClick={() => handleToggleTrending(product.id, product.trending)}
                                      className={`p-1.5 rounded transition-colors ${product.trending ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-300 hover:text-gray-500'}`}
                                      title={product.trending ? 'Remove from trending' : 'Mark as trending'}
                                      data-testid={`trending-toggle-${product.id}`}>
                                      <Flame className="w-4 h-4" />
                                    </button>
                                  </td>
                                  <td className="p-3"><span className={`text-sm ${product.stock < 20 ? 'text-red-600 font-medium' : 'text-gray-600'}`}>{product.stock}</span></td>
                                  <td className="p-3 text-right">
                                    <div className="flex items-center justify-end gap-1">
                                      <button onClick={() => handleEditProduct(product)} className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100" data-testid={`edit-product-${product.id}`}>
                                        <Edit className="w-4 h-4" />
                                      </button>
                                      <button onClick={() => handleDeleteProduct(product.id)} className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-product-${product.id}`}>
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                              {managedProducts.length === 0 && (
                                <tr><td colSpan={7} className="p-10 text-center text-gray-400 text-sm">No products found</td></tr>
                              )}
                            </tbody>
                          </table>
                        )}
                      </div>
                      )}

                      {/* Pagination */}
                      {!showDuplicates && productTotalPages > 1 && (
                        <div className="flex items-center justify-between bg-white border border-gray-200 rounded px-4 py-3">
                          <span className="text-sm text-gray-500">
                            {((productPage - 1) * PRODUCTS_PER_PAGE) + 1}–{Math.min(productPage * PRODUCTS_PER_PAGE, productTotal)} of {productTotal.toLocaleString()}
                          </span>
                          <div className="flex items-center gap-2">
                            <button onClick={() => { setProductPage(p => Math.max(1, p - 1)); setSelectedProducts(new Set()); }}
                              disabled={productPage <= 1}
                              className="px-3 py-1.5 border border-gray-200 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-30"
                              data-testid="products-prev-page">
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                            <span className="text-sm text-gray-700 font-medium">Page {productPage} of {productTotalPages}</span>
                            <button onClick={() => { setProductPage(p => Math.min(productTotalPages, p + 1)); setSelectedProducts(new Set()); }}
                              disabled={productPage >= productTotalPages}
                              className="px-3 py-1.5 border border-gray-200 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-30"
                              data-testid="products-next-page">
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Add Product ── */}
                  {productSubTab === 'add' && (
                    <div className="space-y-6">
                      {/* Quick Add */}
                      <div className="bg-white border border-gray-200 rounded p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Add Product</h3>
                        <form onSubmit={handleQuickAddSubmit} className="space-y-4">
                          <div className="grid md:grid-cols-4 gap-4 items-end">
                            <div className="md:col-span-1 md:row-span-2">
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Image *</label>
                              <div className="relative aspect-[3/4] bg-gray-100 border-2 border-dashed border-gray-300 hover:border-gray-900 cursor-pointer flex items-center justify-center overflow-hidden rounded"
                                onClick={() => fileInputRef.current?.click()}>
                                {quickAddPreview ? <img src={quickAddPreview} alt="Preview" className="w-full h-full object-cover" /> : (
                                  <div className="text-center p-2"><ImagePlus className="w-8 h-8 mx-auto text-gray-300 mb-2" /><span className="text-xs text-gray-400">Click to add</span></div>
                                )}
                                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageSelect} className="hidden" data-testid="quick-add-image" />
                              </div>
                            </div>
                            <div className="md:col-span-1">
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Title *</label>
                              <Input value={quickAddForm.title} onChange={(e) => setQuickAddForm({ ...quickAddForm, title: e.target.value })} placeholder="Poster title" required
                                className="border-gray-200 text-gray-900 placeholder:text-gray-400" data-testid="quick-add-title" />
                            </div>
                            <div className="md:col-span-1">
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Category</label>
                              <Select value={quickAddForm.category} onValueChange={(v) => setQuickAddForm({ ...quickAddForm, category: v, subcategory: '' })}>
                                <SelectTrigger className="border-gray-200 text-gray-900" data-testid="quick-add-category"><SelectValue /></SelectTrigger>
                                <SelectContent>{categories.map((cat) => <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div className="md:col-span-1">
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Sub-Category</label>
                              {(() => {
                                const subs = categories.find(c => c.id === quickAddForm.category)?.subcategories || [];
                                return subs.length > 0 ? (
                                  <Select value={quickAddForm.subcategory || 'none'} onValueChange={(v) => setQuickAddForm({ ...quickAddForm, subcategory: v === 'none' ? '' : v })}>
                                    <SelectTrigger className="border-gray-200 text-gray-900" data-testid="quick-add-subcategory"><SelectValue placeholder="Select..." /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">None</SelectItem>
                                      {subs.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Input disabled placeholder="No subcategories" className="border-gray-200 text-gray-400 bg-gray-50" data-testid="quick-add-subcategory-disabled" />
                                );
                              })()}
                            </div>
                          </div>
                          <div className="grid md:grid-cols-4 gap-4 items-end">
                            <div className="md:col-span-1" />
                            <div className="md:col-span-1">
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Tags</label>
                              <Input value={quickAddForm.tags} onChange={(e) => setQuickAddForm({ ...quickAddForm, tags: e.target.value })} placeholder="tag1, tag2"
                                className="border-gray-200 text-gray-900 placeholder:text-gray-400" data-testid="quick-add-tags" />
                            </div>
                            <div className="md:col-span-1">
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Description</label>
                              <Input value={quickAddForm.description} onChange={(e) => setQuickAddForm({ ...quickAddForm, description: e.target.value })} placeholder="Optional description"
                                className="border-gray-200 text-gray-900 placeholder:text-gray-400" data-testid="quick-add-description" />
                            </div>
                            <div className="md:col-span-1">
                              <button type="submit" disabled={quickAddSaving || !quickAddImage || !quickAddForm.title}
                                className="w-full px-4 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 disabled:opacity-40 flex items-center justify-center gap-2"
                                data-testid="quick-add-submit">
                                {quickAddSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Add</>}
                              </button>
                            </div>
                          </div>
                        </form>
                      </div>

                      {/* Advanced Add */}
                      <div className="bg-white border border-gray-200 rounded p-6">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-bold text-gray-900">Advanced Add</h3>
                          <button onClick={() => { resetProductForm(); setEditingProduct(null); setShowProductModal(true); }}
                            className="px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 flex items-center gap-2"
                            data-testid="add-product-btn">
                            <Plus className="w-4 h-4" /> New Product
                          </button>
                        </div>
                        <p className="text-sm text-gray-500">Use this form for products with custom details, pricing, and image URLs.</p>
                      </div>
                    </div>
                  )}

                  {/* ── AI Upload ── */}
                  {productSubTab === 'ai-upload' && (
                    <AIUploadTab token={token} categories={categories} onProductAdded={() => fetchData()} />
                  )}
                  {/* ── AI Categorize ── */}
                  {productSubTab === 'ai-categorize' && (
                    <AICategorizeTab token={token} onCategoriesChanged={() => fetchData()} />
                  )}
                </div>
              )}

              {/* ─── Pricing ─── */}
              {activeTab === 'pricing' && <PricingTab token={token} />}

              {/* ─── Marketing ─── */}
              {activeTab === 'marketing' && <MarketingTab token={token} />}

              {/* ─── Blog ─── */}
              {activeTab === 'blog' && <BlogTab token={token} />}

              {/* ─── Earnings ─── */}
              {activeTab === 'earnings' && <EarningsTab token={token} />}

              {/* ─── Orders ─── */}
              {activeTab === 'orders' && (
                <div className="space-y-5" data-testid="admin-orders">
                  {/* Header Bar with sub-tabs */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <ShoppingCart className="w-5 h-5" /> Orders
                      </h2>
                      <div className="flex bg-gray-100 rounded p-0.5 ml-3">
                        <button onClick={() => setOrderSubTab('orders')}
                          className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${orderSubTab === 'orders' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
                          data-testid="subtab-orders">Orders</button>
                        <button onClick={() => setOrderSubTab('abandoned')}
                          className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${orderSubTab === 'abandoned' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
                          data-testid="subtab-abandoned">Abandoned Carts</button>
                      </div>
                    </div>
                  </div>

                  {orderSubTab === 'orders' && (
                    <>
                      {/* Bulk Actions Bar */}
                      {selectedOrders.size > 0 && (
                        <div className="bg-gray-900 text-white rounded-lg px-4 py-3 flex items-center justify-between" data-testid="bulk-actions-bar">
                          <span className="text-sm font-medium">{selectedOrders.size} order(s) selected</span>
                          <div className="flex items-center gap-2">
                            <Select onValueChange={(v) => handleBulkStatus(v)}>
                              <SelectTrigger className="w-[130px] h-8 bg-gray-800 border-gray-700 text-white text-xs">
                                <SelectValue placeholder="Set Status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="shipped">Shipped</SelectItem>
                                <SelectItem value="delivered">Delivered</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                            <button onClick={handleBulkDelete}
                              className="h-8 px-3 bg-red-600 text-white text-xs font-bold rounded hover:bg-red-700 flex items-center gap-1"
                              data-testid="bulk-delete-btn">
                              <Trash2 className="w-3 h-3" /> Delete
                            </button>
                            <button onClick={() => setSelectedOrders(new Set())}
                              className="h-8 px-2 text-gray-400 hover:text-white" data-testid="clear-selection-btn">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Stats Summary Row */}
                      <div className="bg-white border border-gray-200 rounded overflow-hidden">
                        <div className="grid grid-cols-5 divide-x divide-gray-200">
                          {(() => {
                            const totalOrders = orders.length;
                            const totalItems = orders.reduce((sum, o) => sum + (o.items?.length || 0), 0);
                            const totalRevenue = orders.filter(o => o.status !== 'cancelled').reduce((sum, o) => sum + (o.total || 0), 0);
                            const fulfilled = orders.filter(o => o.status === 'delivered' || o.status === 'shipped').length;
                            const cancelled = orders.filter(o => o.status === 'cancelled').length;
                            return [
                              { label: 'Total Orders', value: totalOrders },
                              { label: 'Revenue', value: `$${totalRevenue.toFixed(2)}` },
                              { label: 'Items Sold', value: totalItems },
                              { label: 'Fulfilled', value: fulfilled },
                              { label: 'Cancelled', value: cancelled },
                            ].map((s) => (
                              <div key={s.label} className="p-3">
                                <p className="text-xs text-gray-500">{s.label}</p>
                                <p className="text-lg font-bold text-gray-900">{s.value}</p>
                              </div>
                            ));
                          })()}
                        </div>
                      </div>

                      {/* Orders Table */}
                      <div className="bg-white border border-gray-200 rounded overflow-hidden">
                        <table className="w-full" data-testid="orders-table">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                              <th className="p-3 w-8">
                                <input type="checkbox" checked={orders.length > 0 && selectedOrders.size === orders.length}
                                  onChange={toggleAllOrders} className="rounded border-gray-300" data-testid="select-all-orders" />
                              </th>
                              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Order</th>
                              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Customer</th>
                              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Total</th>
                              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Tracking</th>
                              <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Items</th>
                              <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orders.map((order) => {
                              const statusStyle = {
                                pending: 'bg-yellow-100 text-yellow-800',
                                confirmed: 'bg-blue-100 text-blue-800',
                                shipped: 'bg-purple-100 text-purple-800',
                                delivered: 'bg-green-100 text-green-800',
                                cancelled: 'bg-red-100 text-red-800',
                              };
                              return (
                                <tr key={order.id} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${selectedOrders.has(order.id) ? 'bg-blue-50' : ''}`} data-testid={`order-row-${order.id}`}>
                                  <td className="p-3">
                                    <input type="checkbox" checked={selectedOrders.has(order.id)}
                                      onChange={() => toggleOrderSelect(order.id)} className="rounded border-gray-300" data-testid={`select-order-${order.id}`} />
                                  </td>
                                  <td className="p-3">
                                    <span className="text-sm font-medium text-blue-600 cursor-pointer hover:underline" onClick={() => openEditOrder(order)}>
                                      #{order.id.slice(-6).toUpperCase()}
                                    </span>
                                  </td>
                                  <td className="p-3 text-sm text-gray-600">
                                    {new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </td>
                                  <td className="p-3">
                                    <p className="text-sm text-gray-900">{order.shipping_address?.firstName} {order.shipping_address?.lastName}</p>
                                    <p className="text-xs text-gray-400">{order.shipping_address?.city}, {order.shipping_address?.state}</p>
                                  </td>
                                  <td className="p-3 text-sm font-semibold text-gray-900">${(order.total || 0).toFixed(2)}</td>
                                  <td className="p-3">
                                    <select value={order.status || 'pending'}
                                      onChange={(e) => handleSingleStatusUpdate(order.id, e.target.value)}
                                      className={`px-2 py-1 rounded-full text-[11px] font-semibold border-0 cursor-pointer appearance-none bg-no-repeat bg-right pr-6 ${statusStyle[order.status] || 'bg-gray-100 text-gray-600'}`}
                                      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundPosition: 'right 4px center' }}
                                      data-testid={`status-select-${order.id}`}>
                                      <option value="pending">Pending</option>
                                      <option value="confirmed">Confirmed</option>
                                      <option value="shipped">Shipped</option>
                                      <option value="delivered">Delivered</option>
                                      <option value="cancelled">Cancelled</option>
                                    </select>
                                  </td>
                                  <td className="p-3">
                                    <input type="text" defaultValue={order.tracking_number || ''} placeholder="Add tracking"
                                      onBlur={(e) => { const v = e.target.value.trim(); if (v !== (order.tracking_number || '')) handleTrackingUpdate(order.id, v); }}
                                      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                      className="w-[120px] px-2 py-1 border border-gray-200 rounded text-xs text-gray-700 focus:ring-1 focus:ring-gray-900 outline-none"
                                      data-testid={`tracking-input-${order.id}`} />
                                  </td>
                                  <td className="p-3">
                                    <div className="flex gap-1">
                                      {order.items?.slice(0, 2).map((item, i) => (
                                        <img key={i} src={item.image_url} alt="" className="w-7 h-9 object-cover rounded bg-gray-100" />
                                      ))}
                                      {(order.items?.length || 0) > 2 && (
                                        <div className="w-7 h-9 bg-gray-100 rounded flex items-center justify-center text-[9px] text-gray-500 font-bold">
                                          +{order.items.length - 2}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="p-3 text-right">
                                    <div className="flex items-center gap-1 justify-end">
                                      <button onClick={() => openEditOrder(order)} className="h-7 w-7 flex items-center justify-center rounded hover:bg-gray-100" data-testid={`edit-order-${order.id}`} title="Edit">
                                        <Edit2 className="w-3.5 h-3.5 text-gray-500" />
                                      </button>
                                      <button onClick={() => handleDeleteOrder(order.id)} className="h-7 w-7 flex items-center justify-center rounded hover:bg-red-50" data-testid={`delete-order-${order.id}`} title="Delete">
                                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                            {orders.length === 0 && (
                              <tr>
                                <td colSpan={9} className="p-16 text-center">
                                  <ShoppingCart className="w-12 h-12 mx-auto text-gray-200 mb-3" />
                                  <h3 className="text-lg font-bold text-gray-900 mb-1">No orders yet</h3>
                                  <p className="text-sm text-gray-500">Orders will appear here when customers make purchases</p>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Edit Order Modal */}
                      {editingOrder && (
                        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditingOrder(null)}>
                          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-order-modal">
                            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                              <h3 className="text-base font-bold text-gray-900">Edit Order #{editingOrder.slice(-6).toUpperCase()}</h3>
                              <button onClick={() => setEditingOrder(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
                            </div>
                            <div className="p-6 space-y-5">
                              {/* Status & Tracking */}
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Status</label>
                                  <Select value={editForm.status} onValueChange={(v) => setEditForm({...editForm, status: v})}>
                                    <SelectTrigger className="w-full" data-testid="edit-order-status"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="pending">Pending</SelectItem>
                                      <SelectItem value="confirmed">Confirmed</SelectItem>
                                      <SelectItem value="shipped">Shipped</SelectItem>
                                      <SelectItem value="delivered">Delivered</SelectItem>
                                      <SelectItem value="cancelled">Cancelled</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Tracking Number</label>
                                  <input value={editForm.tracking_number} onChange={(e) => setEditForm({...editForm, tracking_number: e.target.value})}
                                    className="w-full px-3 py-2 border rounded text-sm text-gray-900" data-testid="edit-order-tracking" />
                                </div>
                              </div>

                              {/* Shipping Address */}
                              <div>
                                <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">Shipping Address</label>
                                <div className="grid grid-cols-2 gap-3">
                                  {['firstName', 'lastName', 'address', 'city', 'state', 'zip', 'country', 'phone'].map(field => (
                                    <div key={field}>
                                      <label className="text-[10px] text-gray-400 uppercase">{field.replace(/([A-Z])/g, ' $1')}</label>
                                      <input value={editForm.shipping_address?.[field] || ''}
                                        onChange={(e) => setEditForm({...editForm, shipping_address: {...editForm.shipping_address, [field]: e.target.value}})}
                                        className="w-full px-2 py-1.5 border rounded text-sm text-gray-900"
                                        data-testid={`edit-addr-${field}`} />
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Items */}
                              <div>
                                <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">Items</label>
                                <div className="space-y-2">
                                  {editForm.items?.map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded p-2">
                                      <img src={item.image_url} alt="" className="w-10 h-12 object-cover rounded" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-gray-900 truncate">{item.title}</p>
                                        <p className="text-[10px] text-gray-400">{item.size}</p>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <label className="text-[10px] text-gray-400">Qty:</label>
                                        <input type="number" min="1" value={item.quantity}
                                          onChange={(e) => {
                                            const newItems = [...editForm.items];
                                            newItems[idx] = {...newItems[idx], quantity: parseInt(e.target.value) || 1};
                                            setEditForm({...editForm, items: newItems});
                                          }}
                                          className="w-14 px-2 py-1 border rounded text-xs text-center text-gray-900" data-testid={`edit-item-qty-${idx}`} />
                                        <label className="text-[10px] text-gray-400">Price:</label>
                                        <input type="number" step="0.01" value={item.price}
                                          onChange={(e) => {
                                            const newItems = [...editForm.items];
                                            newItems[idx] = {...newItems[idx], price: parseFloat(e.target.value) || 0};
                                            setEditForm({...editForm, items: newItems});
                                          }}
                                          className="w-20 px-2 py-1 border rounded text-xs text-gray-900" data-testid={`edit-item-price-${idx}`} />
                                        <button onClick={() => {
                                          const newItems = editForm.items.filter((_, i) => i !== idx);
                                          setEditForm({...editForm, items: newItems});
                                        }} className="text-red-400 hover:text-red-600" data-testid={`remove-item-${idx}`}>
                                          <X className="w-4 h-4" />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Notes */}
                              <div>
                                <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Notes</label>
                                <textarea value={editForm.notes} onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                                  rows={2} className="w-full px-3 py-2 border rounded text-sm text-gray-900 resize-none" data-testid="edit-order-notes" />
                              </div>
                            </div>
                            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-3 flex justify-end gap-2">
                              <button onClick={() => setEditingOrder(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                              <button onClick={handleSaveOrder} className="px-5 py-2 bg-gray-900 text-white text-sm font-bold rounded hover:bg-gray-700" data-testid="save-order-btn">Save Changes</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Abandoned Carts Sub-tab */}
                  {orderSubTab === 'abandoned' && (
                    <div className="space-y-4" data-testid="abandoned-carts">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-white border rounded p-4">
                          <p className="text-xs text-gray-500">Abandoned Carts</p>
                          <p className="text-2xl font-bold text-gray-900">{abandonedCarts.total_count}</p>
                        </div>
                        <div className="bg-white border rounded p-4">
                          <p className="text-xs text-gray-500">Lost Revenue</p>
                          <p className="text-2xl font-bold text-red-600">${abandonedCarts.total_value.toFixed(2)}</p>
                        </div>
                        <div className="bg-white border rounded p-4">
                          <p className="text-xs text-gray-500">Avg Cart Value</p>
                          <p className="text-2xl font-bold text-gray-900">
                            ${abandonedCarts.total_count > 0 ? (abandonedCarts.total_value / abandonedCarts.total_count).toFixed(2) : '0.00'}
                          </p>
                        </div>
                      </div>

                      {abandonedCarts.carts.length === 0 ? (
                        <div className="bg-white border rounded p-10 text-center">
                          <ShoppingCart className="w-10 h-10 mx-auto text-gray-200 mb-3" />
                          <h3 className="text-base font-bold text-gray-900 mb-1">No abandoned carts</h3>
                          <p className="text-sm text-gray-500">Carts inactive for 30+ minutes will appear here</p>
                        </div>
                      ) : (
                        <div className="bg-white border rounded overflow-hidden">
                          <table className="w-full">
                            <thead className="bg-gray-50 border-b">
                              <tr>
                                <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Session</th>
                                <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Email</th>
                                <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Items</th>
                                <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Cart Value</th>
                                <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Last Active</th>
                                <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Products</th>
                                <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                                <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {abandonedCarts.carts.map((cart) => (
                                <tr key={cart.session_id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`abandoned-cart-${cart.session_id}`}>
                                  <td className="p-3 text-xs font-mono text-gray-500">{cart.session_id.slice(-12)}</td>
                                  <td className="p-3 text-xs text-gray-700">
                                    {cart.user_email || cart.recovery_email || <span className="text-gray-400 italic">No email</span>}
                                  </td>
                                  <td className="p-3 text-sm font-medium text-gray-900">{cart.item_count}</td>
                                  <td className="p-3 text-sm font-bold text-gray-900">${cart.cart_value.toFixed(2)}</td>
                                  <td className="p-3 text-xs text-gray-500">
                                    {new Date(cart.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </td>
                                  <td className="p-3">
                                    <div className="flex gap-1">
                                      {cart.items?.slice(0, 3).map((item, i) => (
                                        <div key={i} className="text-[10px] text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded truncate max-w-[80px]">{item.title || item.size}</div>
                                      ))}
                                    </div>
                                  </td>
                                  <td className="p-3">
                                    {cart.recovery_sent ? (
                                      <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Sent</span>
                                    ) : (
                                      <span className="text-[10px] bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">Pending</span>
                                    )}
                                  </td>
                                  <td className="p-3 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                      <button
                                        onClick={() => {
                                          setRecoveryModal(cart);
                                          setRecoveryEmail(cart.user_email || cart.recovery_email || '');
                                          setRecoveryDiscount('');
                                        }}
                                        className="text-xs bg-blue-600 text-white px-2.5 py-1 rounded hover:bg-blue-700 font-medium"
                                        data-testid={`recover-cart-${cart.session_id}`}
                                      >
                                        {cart.recovery_sent ? 'Resend' : 'Recover'}
                                      </button>
                                      <button onClick={async () => {
                                        await axios.delete(`${API}/admin/abandoned-carts/${cart.session_id}`, { headers: { Authorization: `Bearer ${token}` } });
                                        fetchAbandonedCarts();
                                      }} className="text-xs text-red-500 hover:text-red-700">Dismiss</button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Recovery Email Modal */}
                      {recoveryModal && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="recovery-email-modal">
                          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                            <div className="p-5 border-b">
                              <h3 className="text-lg font-bold text-gray-900">Send Recovery Email</h3>
                              <p className="text-sm text-gray-500 mt-1">Cart value: <strong>${recoveryModal.cart_value?.toFixed(2)}</strong> ({recoveryModal.item_count} items)</p>
                            </div>
                            <div className="p-5 space-y-4">
                              <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Recipient Email *</label>
                                <input
                                  type="email"
                                  value={recoveryEmail}
                                  onChange={(e) => setRecoveryEmail(e.target.value)}
                                  placeholder="customer@example.com"
                                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                                  data-testid="recovery-email-input"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Discount Code (optional)</label>
                                <input
                                  type="text"
                                  value={recoveryDiscount}
                                  onChange={(e) => setRecoveryDiscount(e.target.value)}
                                  placeholder="e.g. COMEBACK10"
                                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900"
                                  data-testid="recovery-discount-input"
                                />
                                <p className="text-[10px] text-gray-400 mt-1">Include a discount code to incentivize the purchase</p>
                              </div>
                              <div className="bg-gray-50 border rounded p-3">
                                <p className="text-xs text-gray-500 font-medium mb-1">Items in cart:</p>
                                {recoveryModal.items?.map((item, i) => (
                                  <p key={i} className="text-xs text-gray-700">{item.title || 'Poster'} — {item.size} x{item.quantity} — ${(item.price * item.quantity).toFixed(2)}</p>
                                ))}
                              </div>
                            </div>
                            <div className="p-5 border-t bg-gray-50 flex gap-3 justify-end rounded-b-lg">
                              <button
                                onClick={() => { setRecoveryModal(null); setRecoveryEmail(''); setRecoveryDiscount(''); }}
                                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                                data-testid="recovery-cancel-btn"
                              >Cancel</button>
                              <button
                                onClick={() => handleSendRecovery(recoveryModal)}
                                disabled={recoverySending || !recoveryEmail.trim()}
                                className="px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="recovery-send-btn"
                              >
                                {recoverySending ? 'Sending...' : 'Send Recovery Email'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ─── Promotions ─── */}
              {activeTab === 'promotions' && (
                <div className="space-y-4" data-testid="admin-promotions">
                  <div className="flex justify-between items-center">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Promotions</h2>
                      <p className="text-sm text-gray-500">Manage discounts, sales, and promo codes</p>
                    </div>
                  </div>

                  {/* Sub-tabs: Promotions vs Discount Codes */}
                  <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
                    <button onClick={() => setPromoSubTab('promotions')}
                      className={`px-4 py-2 text-sm font-medium rounded transition-colors ${promoSubTab === 'promotions' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                      data-testid="promo-sub-promotions">
                      Promotions
                    </button>
                    <button onClick={() => setPromoSubTab('codes')}
                      className={`px-4 py-2 text-sm font-medium rounded transition-colors ${promoSubTab === 'codes' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                      data-testid="promo-sub-codes">
                      Discount Codes
                    </button>
                  </div>

                  {promoSubTab === 'promotions' && (
                  <>
                  <div className="flex justify-end gap-2">
                      <button onClick={handleClearAllPromotions}
                        className="px-4 py-2 border border-red-200 text-red-600 text-sm font-medium rounded hover:bg-red-50"
                        data-testid="clear-all-promos">
                        Clear All
                      </button>
                      <button onClick={() => { resetPromoForm(); setEditingPromo(null); setShowPromoModal(true); }}
                        className="px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 flex items-center gap-2"
                        data-testid="add-promo-btn">
                        <Plus className="w-4 h-4" /> New Promotion
                      </button>
                  </div>

                  {promotions.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded p-10 text-center">
                      <Tag className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                      <h3 className="text-lg font-bold text-gray-900 mb-1">No Promotions</h3>
                      <p className="text-sm text-gray-500">Create your first promotion</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {promotions.map((promo) => (
                        <div key={promo.id} className={`bg-white border rounded p-5 ${promo.is_active ? 'border-green-300' : 'border-gray-200'}`}
                          data-testid={`promo-${promo.id}`}>
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-base font-bold text-gray-900">{promo.name}</h3>
                                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${promo.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                  {promo.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                              {promo.description && <p className="text-sm text-gray-500 mb-2">{promo.description}</p>}
                              <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                                <span className="flex items-center gap-1">
                                  {promo.discount_type === 'bogo' ? (
                                    <><Gift className="w-3.5 h-3.5 text-orange-500" /><strong>Buy {promo.bogo_buy || 1} Get {promo.bogo_get || 1} Free</strong></>
                                  ) : (
                                    <><Percent className="w-3.5 h-3.5 text-orange-500" /><strong>{promo.discount_type === 'percentage' ? `${promo.discount_value}%` : `$${promo.discount_value}`} OFF</strong></>
                                  )}
                                </span>
                                <span>Applies to: <strong className="capitalize">{promo.applies_to}</strong>{promo.category && ` (${promo.category})`}</span>
                                <span>Affected: <strong>{promo.products_affected}</strong></span>
                              </div>
                            </div>
                            <div className="flex gap-1.5">
                              <button onClick={() => handleTogglePromo(promo)}
                                className={`px-3 py-1.5 text-xs font-semibold rounded ${promo.is_active ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-green-600 text-white hover:bg-green-700'}`}
                                data-testid={`toggle-promo-${promo.id}`}>
                                {promo.is_active ? 'Deactivate' : 'Activate'}
                              </button>
                              <button onClick={() => handleEditPromo(promo)} className="p-1.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100" data-testid={`edit-promo-${promo.id}`}>
                                <Edit className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleDeletePromo(promo.id)} className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-promo-${promo.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Quick Templates */}
                  <div className="bg-white border border-gray-200 rounded p-5">
                    <h3 className="text-sm font-bold text-gray-900 mb-3">Quick Templates</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[10, 20, 25, 50].map(val => (
                        <button key={val} onClick={() => {
                          setPromoForm({ name: `${val}% Off Everything`, description: `Site-wide ${val}% discount`, discount_type: 'percentage', discount_value: String(val), applies_to: 'all', category: '', is_active: true });
                          setEditingPromo(null); setShowPromoModal(true);
                        }} className="p-3 bg-gray-50 hover:bg-gray-100 rounded text-center transition-colors">
                          <span className="text-2xl font-bold text-gray-900">{val}%</span>
                          <p className="text-xs text-gray-500 mt-0.5">Off Everything</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* BOGO Deal Templates */}
                  <div className="bg-white border border-gray-200 rounded p-5" data-testid="bogo-templates">
                    <div className="flex items-center gap-2 mb-3">
                      <Gift className="w-4 h-4 text-orange-500" />
                      <h3 className="text-sm font-bold text-gray-900">BOGO Deals</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {[
                        { buy: 1, get: 1, label: 'Buy 1 Get 1 Free', desc: 'Customers buy 1, get 1 free' },
                        { buy: 2, get: 1, label: 'Buy 2 Get 1 Free', desc: 'Customers buy 2, get 1 free' },
                        { buy: 3, get: 2, label: 'Buy 3 Get 2 Free', desc: 'Customers buy 3, get 2 free' },
                      ].map((bogo) => (
                        <button key={`${bogo.buy}-${bogo.get}`} onClick={() => {
                          setPromoForm({
                            name: bogo.label,
                            description: bogo.desc,
                            discount_type: 'bogo',
                            discount_value: '0',
                            applies_to: 'all',
                            category: '',
                            is_active: true,
                            bogo_buy: String(bogo.buy),
                            bogo_get: String(bogo.get),
                          });
                          setEditingPromo(null); setShowPromoModal(true);
                        }} className="p-4 bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 hover:border-orange-400 rounded text-left transition-all group"
                          data-testid={`bogo-${bogo.buy}-${bogo.get}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Gift className="w-5 h-5 text-orange-500 group-hover:scale-110 transition-transform" />
                            <span className="text-base font-bold text-gray-900">{bogo.label}</span>
                          </div>
                          <p className="text-xs text-gray-500">{bogo.desc}</p>
                          <div className="mt-2 flex items-center gap-1">
                            <span className="text-[10px] text-orange-600 font-semibold uppercase tracking-wider">Click to create</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  </>
                  )}

                  {/* ─── Discount Codes Sub-Tab ─── */}
                  {promoSubTab === 'codes' && (
                    <PromotionsCodesSection token={token} />
                  )}
                </div>
              )}

              {/* ─── Users ─── */}
              {activeTab === 'users' && (
                <div className="space-y-4" data-testid="admin-users">
                  <div className="flex justify-between items-center">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">User Management</h2>
                      <p className="text-sm text-gray-500">Manage staff accounts, permissions, and track activity</p>
                    </div>
                    <button onClick={() => { resetUserForm(); setShowUserModal(true); }}
                      className="px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 flex items-center gap-2"
                      data-testid="add-user-btn">
                      <UserPlus className="w-4 h-4" /> Add Staff User
                    </button>
                  </div>

                  {/* User Activity Summary */}
                  <UserActivityPanel token={token} />

                  {/* Role Legend */}
                  <div className="flex flex-wrap gap-3 bg-white border border-gray-200 rounded p-3">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mr-1">Roles:</span>
                    {Object.entries(ROLE_COLORS).map(([role, color]) => (
                      <span key={role} className="flex items-center gap-1.5 text-xs text-gray-700">
                        <span className={`w-2 h-2 rounded-full ${color}`} /><span className="capitalize">{role}</span>
                      </span>
                    ))}
                  </div>

                  {/* Users Table */}
                  <div className="bg-white border border-gray-200 rounded overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                          <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                          <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="text-left p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Joined</th>
                          <th className="text-right p-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                            <td className="p-3">
                              <p className="text-sm font-medium text-gray-900">{u.name}</p>
                              <p className="text-xs text-gray-400">{u.email}</p>
                            </td>
                            <td className="p-3">
                              <Select value={u.role || 'customer'} onValueChange={(val) => handleUpdateUserRole(u.id, val)} disabled={u.id === user?.id}>
                                <SelectTrigger className="w-[120px] border-gray-200 text-gray-700 text-sm h-8" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="admin">Admin</SelectItem>
                                  <SelectItem value="manager">Manager</SelectItem>
                                  <SelectItem value="editor">Editor</SelectItem>
                                  <SelectItem value="inventory">Inventory</SelectItem>
                                  <SelectItem value="viewer">Viewer</SelectItem>
                                  <SelectItem value="customer">Customer</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-3">
                              <button onClick={() => handleToggleUserActive(u.id, u.is_active !== false)} disabled={u.id === user?.id}
                                className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${u.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} ${u.id === user?.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}
                                data-testid={`user-status-${u.id}`}>
                                {u.is_active !== false ? 'Active' : 'Disabled'}
                              </button>
                            </td>
                            <td className="p-3 text-xs text-gray-400">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                            <td className="p-3 text-right">
                              {u.id !== user?.id ? (
                                <button onClick={() => handleDeleteUser(u.id)} className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-user-${u.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              ) : <span className="text-[10px] text-gray-400 uppercase">You</span>}
                            </td>
                          </tr>
                        ))}
                        {users.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-gray-400 text-sm">No users found</td></tr>}
                      </tbody>
                    </table>
                  </div>

                  {/* Permissions Reference */}
                  <div className="bg-white border border-gray-200 rounded p-5">
                    <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2"><Shield className="w-4 h-4" /> Permission Reference</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                      {Object.entries(PERMISSIONS_MAP).filter(([k]) => k !== 'customer').map(([role, perms]) => (
                        <div key={role} className="bg-gray-50 rounded p-3">
                          <div className="flex items-center gap-1.5 mb-2">
                            <span className={`w-2 h-2 rounded-full ${ROLE_COLORS[role]}`} />
                            <h4 className="text-xs font-bold text-gray-900 uppercase">{role}</h4>
                          </div>
                          <ul className="text-[11px] text-gray-500 space-y-0.5">
                            {perms.map((p) => <li key={p} className="capitalize">{p.replace(/_/g, ' ')}</li>)}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ─── Bulk Import ─── */}
              {activeTab === 'import' && (
                <div className="space-y-4" data-testid="admin-import">
                  <div className="flex justify-between items-center">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Bulk Import</h2>
                      <p className="text-sm text-gray-500">Import products from Excel or CSV</p>
                    </div>
                    <button onClick={handleUpdateAllSizes}
                      className="px-4 py-2 border border-gray-200 text-gray-700 text-sm font-medium rounded hover:bg-gray-50 flex items-center gap-2"
                      data-testid="update-sizes-btn">
                      <RefreshCw className="w-4 h-4" /> Update All Sizes
                    </button>
                  </div>

                  {/* Size Reference */}
                  <div className="bg-white border border-gray-200 rounded p-4">
                    <h3 className="text-sm font-bold text-gray-900 mb-3">Size Pricing</h3>
                    <div className="grid grid-cols-5 gap-3">
                      {DEFAULT_SIZES.map((size) => (
                        <div key={size.size} className="text-center bg-gray-50 rounded p-2">
                          <p className="text-lg font-bold text-gray-900">{size.size}"</p>
                          <p className="text-sm font-semibold text-orange-600">${size.price.toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <ExcelImportSection token={token} onComplete={fetchData} />

                  {/* CSV Import */}
                  <div className="bg-white border border-gray-200 rounded p-5">
                    <h3 className="text-sm font-bold text-gray-900 mb-3">Quick CSV/JSON Import</h3>
                    {/* CSV Parameter Guide */}
                    <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4 text-xs">
                      <p className="font-bold text-gray-900 mb-2">CSV Format — Required & Optional Fields:</p>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                        <div><span className="text-red-500 font-bold">title*</span> — Product name</div>
                        <div><span className="text-red-500 font-bold">category*</span> — e.g. movies, music, sports</div>
                        <div><span className="text-red-500 font-bold">image_url*</span> — Direct image URL</div>
                        <div><span className="text-gray-500 font-semibold">description</span> — Product description</div>
                        <div><span className="text-gray-500 font-semibold">subcategory</span> — e.g. movies_action</div>
                        <div><span className="text-gray-500 font-semibold">sku</span> — Unique product SKU</div>
                        <div><span className="text-gray-500 font-semibold">price</span> — Override default price</div>
                        <div><span className="text-gray-500 font-semibold">tags</span> — Semicolon-separated</div>
                        <div><span className="text-gray-500 font-semibold">stock</span> — Inventory count (default: 100)</div>
                        <div><span className="text-gray-500 font-semibold">artist</span> — Artist/creator name</div>
                      </div>
                      <p className="mt-2 text-gray-500">* = required. First line must be a header row.</p>
                      <p className="mt-1 text-gray-500">Example: <code className="bg-gray-200 px-1 rounded">title,category,subcategory,image_url,sku,price</code></p>
                    </div>
                    <Textarea value={bulkImportData} onChange={(e) => setBulkImportData(e.target.value)}
                      placeholder="title, description, category, image_url, tags" rows={5}
                      className="mb-3 border-gray-200 text-gray-900 placeholder:text-gray-400" data-testid="bulk-import-data" />
                    <button onClick={handleBulkImport} disabled={bulkImporting || !bulkImportData.trim()}
                      className="px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 disabled:opacity-40 flex items-center gap-2"
                      data-testid="bulk-import-btn">
                      {bulkImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {bulkImporting ? 'Importing...' : 'Import'}
                    </button>
                  </div>
                </div>
              )}

              {/* ─── SEO ─── */}
              {activeTab === 'seo' && <SEOTab token={token} />}

              {activeTab === 'notifications' && <NotificationsTab token={token} />}

              {/* ─── Categories ─── */}
              {activeTab === 'categories' && <CategoriesTab token={token} />}

              {/* ─── Snapshots ─── */}
              {activeTab === 'snapshots' && <SnapshotsTab token={token} />}

              {/* ─── Construction ─── */}
              {activeTab === 'construction' && <ConstructionTab token={token} />}

              {/* ─── Settings ─── */}
              {activeTab === 'settings' && <SettingsTab token={token} />}
            </>
          )}
        </div>
      </main>

      {/* ─── Product Modal ─── */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleProductSubmit} className="space-y-4 mt-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Title</label>
              <Input value={productForm.title} onChange={(e) => setProductForm({ ...productForm, title: e.target.value })} required
                className="border-gray-200 text-gray-900" data-testid="product-title" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">SKU</label>
                <Input value={productForm.sku || ''} onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })} placeholder="e.g. PF-MOV-001"
                  className="border-gray-200 text-gray-900" data-testid="product-sku" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Base Price ($)</label>
                <Input type="number" step="0.01" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} required
                  className="border-gray-200 text-gray-900" data-testid="product-price" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Description</label>
              <Textarea value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} rows={3} required
                className="border-gray-200 text-gray-900" data-testid="product-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Category</label>
                <Select value={productForm.category} onValueChange={(value) => setProductForm({ ...productForm, category: value, subcategory: '' })}>
                  <SelectTrigger className="border-gray-200 text-gray-900" data-testid="product-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Sub-Category</label>
                {(() => {
                  const subs = categories.find(c => c.id === productForm.category)?.subcategories || [];
                  return subs.length > 0 ? (
                    <Select value={productForm.subcategory || 'none'} onValueChange={(v) => setProductForm({ ...productForm, subcategory: v === 'none' ? '' : v })}>
                      <SelectTrigger className="border-gray-200 text-gray-900" data-testid="product-subcategory"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {subs.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input disabled placeholder="No subcategories" className="border-gray-200 text-gray-400 bg-gray-50" data-testid="product-subcategory-disabled" />
                  );
                })()}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Image URL</label>
              <Input value={productForm.image_url} onChange={(e) => setProductForm({ ...productForm, image_url: e.target.value })} placeholder="https://..." required
                className="border-gray-200 text-gray-900" data-testid="product-image" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Artist</label>
                <Input value={productForm.artist} onChange={(e) => setProductForm({ ...productForm, artist: e.target.value })}
                  className="border-gray-200 text-gray-900" data-testid="product-artist" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Stock</label>
                <Input type="number" value={productForm.stock} onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })} required
                  className="border-gray-200 text-gray-900" data-testid="product-stock" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Tags (comma separated)</label>
              <Input value={productForm.tags} onChange={(e) => setProductForm({ ...productForm, tags: e.target.value })} placeholder="art, modern"
                className="border-gray-200 text-gray-900" data-testid="product-tags" />
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={productForm.featured} onChange={(e) => setProductForm({ ...productForm, featured: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300" data-testid="product-featured" />
                <span className="text-sm text-gray-700">Featured</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={productForm.trending} onChange={(e) => setProductForm({ ...productForm, trending: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 accent-orange-500" data-testid="product-trending" />
                <span className="text-sm text-gray-700 flex items-center gap-1"><Flame className="w-3.5 h-3.5 text-orange-500" /> Trending</span>
              </label>
            </div>
            <div className="flex gap-3 pt-3">
              <button type="button" onClick={() => setShowProductModal(false)}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving}
                className="flex-1 px-4 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 disabled:opacity-40 flex items-center justify-center gap-2"
                data-testid="save-product-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingProduct ? 'Update' : 'Create')}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─── Promotion Modal ─── */}
      <Dialog open={showPromoModal} onOpenChange={setShowPromoModal}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">
              {editingPromo ? 'Edit Promotion' : 'Create Promotion'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePromoSubmit} className="space-y-4 mt-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Name *</label>
              <Input value={promoForm.name} onChange={(e) => setPromoForm({ ...promoForm, name: e.target.value })} placeholder="e.g., Summer Sale 20% Off" required
                className="border-gray-200 text-gray-900" data-testid="promo-name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Description</label>
              <Textarea value={promoForm.description} onChange={(e) => setPromoForm({ ...promoForm, description: e.target.value })} rows={2}
                className="border-gray-200 text-gray-900" data-testid="promo-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Discount Type</label>
                <Select value={promoForm.discount_type} onValueChange={(value) => setPromoForm({ ...promoForm, discount_type: value })}>
                  <SelectTrigger className="border-gray-200 text-gray-700" data-testid="promo-discount-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed ($)</SelectItem>
                    <SelectItem value="bogo">BOGO (Buy X Get Y Free)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {promoForm.discount_type === 'bogo' ? (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Buy</label>
                    <Input type="number" min="1" value={promoForm.bogo_buy || '1'} onChange={(e) => setPromoForm({ ...promoForm, bogo_buy: e.target.value })} required
                      className="border-gray-200 text-gray-900" data-testid="promo-bogo-buy" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Get Free</label>
                    <Input type="number" min="1" value={promoForm.bogo_get || '1'} onChange={(e) => setPromoForm({ ...promoForm, bogo_get: e.target.value })} required
                      className="border-gray-200 text-gray-900" data-testid="promo-bogo-get" />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Value *</label>
                  <Input type="number" step={promoForm.discount_type === 'percentage' ? '1' : '0.01'} min="0"
                    max={promoForm.discount_type === 'percentage' ? '100' : undefined}
                    value={promoForm.discount_value} onChange={(e) => setPromoForm({ ...promoForm, discount_value: e.target.value })} required
                    className="border-gray-200 text-gray-900" data-testid="promo-discount-value" />
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Apply To</label>
              <Select value={promoForm.applies_to} onValueChange={(value) => setPromoForm({ ...promoForm, applies_to: value })}>
                <SelectTrigger className="border-gray-200 text-gray-700" data-testid="promo-applies-to"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Products</SelectItem>
                  <SelectItem value="category">Specific Category</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {promoForm.applies_to === 'category' && (
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Category</label>
                <Select value={promoForm.category} onValueChange={(value) => setPromoForm({ ...promoForm, category: value })}>
                  <SelectTrigger className="border-gray-200 text-gray-700" data-testid="promo-category"><SelectValue placeholder="Choose..." /></SelectTrigger>
                  <SelectContent>{categories.map((cat) => <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={promoForm.is_active} onChange={(e) => setPromoForm({ ...promoForm, is_active: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300" data-testid="promo-active" />
              <span className="text-sm text-gray-700">Activate immediately</span>
            </label>
            <div className="flex gap-3 pt-3">
              <button type="button" onClick={() => setShowPromoModal(false)}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={promoSaving}
                className="flex-1 px-4 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 disabled:opacity-40 flex items-center justify-center gap-2"
                data-testid="save-promo-btn">
                {promoSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingPromo ? 'Update' : 'Create & Apply')}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ─── User Modal ─── */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">Add Staff User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4 mt-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Name *</label>
              <Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} placeholder="Full name" required
                className="border-gray-200 text-gray-900" data-testid="user-name-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Email *</label>
              <Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} placeholder="user@example.com" required
                className="border-gray-200 text-gray-900" data-testid="user-email-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Password *</label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  placeholder="Min 8 chars, letters + numbers" required
                  className="border-gray-200 text-gray-900 pr-10" data-testid="user-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="toggle-password-visibility">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Role *</label>
              <Select value={userForm.role} onValueChange={(val) => setUserForm({ ...userForm, role: val })}>
                <SelectTrigger className="border-gray-200 text-gray-700" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin — Full access</SelectItem>
                  <SelectItem value="manager">Manager — Orders + products</SelectItem>
                  <SelectItem value="editor">Editor — Products + promos</SelectItem>
                  <SelectItem value="inventory">Inventory — Add products only</SelectItem>
                  <SelectItem value="viewer">Viewer — Read-only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-3">
              <button type="button" onClick={() => setShowUserModal(false)}
                className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={userSaving}
                className="flex-1 px-4 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 disabled:opacity-40 flex items-center justify-center gap-2"
                data-testid="save-user-btn">
                {userSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create User'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
